import React, { useState, useMemo } from 'react';
import '../../styles/guide-modal.css';

/**
 * Helper to extract searchable text from JSX content
 */
function extractTextFromJSX(jsx) {
  if (!jsx) return '';
  if (typeof jsx === 'string' || typeof jsx === 'number') return String(jsx);

  if (Array.isArray(jsx)) {
    return jsx.map(extractTextFromJSX).join(' ');
  }

  if (jsx.props && jsx.props.children) {
    return extractTextFromJSX(jsx.props.children);
  }

  return '';
}

/**
 * GuideModal - Comprehensive game guide (Civ 6 style Civilopedia)
 * Left sidebar: Categories with search
 * Right panel: Detailed technical information
 */
function GuideModal({ onClose, initialCategory = 'getting-started', initialTopic = 'basics' }) {
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedTopic, setSelectedTopic] = useState(initialTopic);
  const [searchQuery, setSearchQuery] = useState('');

  // Guide content structure with MUCH more detail
  const guideContent = {
    'getting-started': {
      name: 'Getting Started',
      icon: '🎮',
      topics: {
        'basics': {
          name: 'Game Basics',
          icon: '📖',
          content: (
            <>
              <h3>Game Overview</h3>
              <p>Medieval fantasy incremental: build kingdom through 11 eras. <strong>Victory:</strong> Build Dimensional Beacon (Tier 11 Wonder, Quest 100).</p>

              <h4>Core Systems</h4>
              <table className="guide-table">
                <tbody>
                  <tr><td><strong>Buildings</strong></td><td>76+ buildings, 11 tiers. Cost: ×2 base, +20%/owned. Automate production.</td></tr>
                  <tr><td><strong>Technologies</strong></td><td>70 techs, 11 tiers. Unlock buildings/resources/advisors. Queue holds 5.</td></tr>
                  <tr><td><strong>Quests</strong></td><td>100+ objectives, 10 chapters. Follow for guided progression.</td></tr>
                  <tr><td><strong>World Map</strong></td><td>Hex-based, 50+ biomes. Gathering, tile buildings, expeditions.</td></tr>
                  <tr><td><strong>Advisors</strong></td><td>17 advisors (2-5 slots). Bonuses + penalties + special abilities.</td></tr>
                  <tr><td><strong>Wonders</strong></td><td>33 wonders, 11 tiers. Permanent global bonuses. Timestamp-based.</td></tr>
                </tbody>
              </table>

              <h4>Advanced Systems</h4>
              <table className="guide-table">
                <tbody>
                  <tr><td><strong>Outposts</strong></td><td>Build settlements on map tiles. 4 building slots, hire mercenaries, send on missions.</td></tr>
                  <tr><td><strong>Mercenaries</strong></td><td>4 classes (Warrior, Ranger, Mage, Rogue). Form squads for automated dungeon runs.</td></tr>
                  <tr><td><strong>Star Force</strong></td><td>Enhance equipment with stars (max 15). Uses Star Essence from dungeons.</td></tr>
                  <tr><td><strong>Forging</strong></td><td>Apply scrolls to equipment slots. 5 gemstone types, 5 scroll tiers.</td></tr>
                  <tr><td><strong>Cubing</strong></td><td>Add potential stats to equipment. 4 grades: Rare → Epic → Unique → Legendary.</td></tr>
                  <tr><td><strong>Great Works</strong></td><td>Map-bound wonders with biome cluster requirements. Max 5 total.</td></tr>
                  <tr><td><strong>Trade Routes</strong></td><td>Connect tiles to Kingdom. Each connected tile building grants +5 panel capacity.</td></tr>
                  <tr><td><strong>Life Skills</strong></td><td>7 proficiencies (5 gathering, 2 crafting). Level up for bonuses.</td></tr>
                </tbody>
              </table>

              <h4>Key Mechanics</h4>
              <ul>
                <li><strong>Auto-save:</strong> 30s intervals, localStorage. Timestamps persist when closed.</li>
                <li><strong>No caps:</strong> Unlimited resources - stockpile freely.</li>
                <li><strong>Era:</strong> Based on highest researched tech tier (not buildings).</li>
                <li><strong>Scaling:</strong> Buildings +20%/owned. Techs/quests per-tier multipliers.</li>
              </ul>
            </>
          ),
        },
        'progression': {
          name: 'Progression Path',
          icon: '🛤️',
          content: (
            <>
              <h3>Progression Roadmap</h3>

              <table className="guide-table">
                <thead>
                  <tr><th>Phase</th><th>Buildings</th><th>Map/Other</th><th>Goals</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Eras 1-3</strong><br/>30-60min</td>
                    <td>5-10 Woodcutter/Stone Picker/Farm<br/>3-5 Houses, Markets<br/>Quarries, Mines</td>
                    <td>Explore 10 tiles<br/>Tier 1-2 techs</td>
                    <td>Chapters 1-3<br/>Basic economy</td>
                  </tr>
                  <tr>
                    <td><strong>Eras 4-7</strong><br/>1-3hrs</td>
                    <td>30-50 total<br/>First advisor/wonder<br/>3-5 specializations</td>
                    <td>30-50 tiles<br/>3-4 advisor slots<br/>2-3 research upgrades</td>
                    <td>Chapters 4-6<br/>Unlock advanced resources</td>
                  </tr>
                  <tr>
                    <td><strong>Eras 8-10</strong><br/>3-8hrs</td>
                    <td>100+ total<br/>20+ Tier 8-9 each<br/>10-15 specializations</td>
                    <td>100+ tiles<br/>5 advisor slots<br/>6 research upgrades<br/>5-8 wonders</td>
                    <td>Chapters 7-9<br/>Wonder collection</td>
                  </tr>
                  <tr>
                    <td><strong>Era 11</strong><br/>8-20hrs</td>
                    <td>200+ total<br/>10-20 Tier 11 each<br/>10× multipliers</td>
                    <td>200+ tiles optimized<br/>10-15 wonders<br/>Millions stockpiled</td>
                    <td>Quest 100<br/>Dimensional Beacon<br/>Victory!</td>
                  </tr>
                </tbody>
              </table>

              <h4>Progression Bottlenecks & Solutions</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Bottleneck</th><th>Cause</th><th>Solution</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Slow research</td>
                    <td>Low knowledge production</td>
                    <td>Build Libraries, hire Scribe advisor, build Great Library wonder, purchase research speed upgrades</td>
                  </tr>
                  <tr>
                    <td>Can't afford buildings</td>
                    <td>Resource deficit</td>
                    <td>Check production breakdowns, specialize key buildings, hire matching advisors, explore World Map for gathering</td>
                  </tr>
                  <tr>
                    <td>Population stuck</td>
                    <td>Not enough food</td>
                    <td>Build more Farms/Windmills, specialize food buildings, hire food-boosting advisor, build Great Granary</td>
                  </tr>
                  <tr>
                    <td>Wonder too expensive</td>
                    <td>Late-game resource requirements</td>
                    <td>Let production run overnight, build production chain buildings, stack multipliers, enable auto-gather on World Map</td>
                  </tr>
                  <tr>
                    <td>Quest objective stuck</td>
                    <td>Missing prerequisite</td>
                    <td>Check quest objectives carefully, may need specific tech/building/resource unlocked first</td>
                  </tr>
                </tbody>
              </table>
            </>
          ),
        },
        'interface': {
          name: 'Interface Guide',
          icon: '🖥️',
          content: (
            <>
              <h3>Complete User Interface Documentation</h3>

              <h4>Top Navigation Bar</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Element</th><th>Description</th><th>Technical Details</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>⚔️ Civ-IDLE</strong></td>
                    <td>Game title and kingdom tier</td>
                    <td>Shows current tier (1-11) based on highest researched tech</td>
                  </tr>
                  <tr>
                    <td><strong>📖 Guide</strong></td>
                    <td>Opens this comprehensive guide</td>
                    <td>Full-text search across 2000+ lines of documentation</td>
                  </tr>
                  <tr>
                    <td><strong>👑 Advisors</strong></td>
                    <td>Advisor management</td>
                    <td>Hire, fire, view bonuses/penalties. Max 5 slots (unlockable via tech)</td>
                  </tr>
                  <tr>
                    <td><strong>📊 Stats</strong></td>
                    <td>Kingdom statistics</td>
                    <td>Total production, buildings built, resources gathered, time played</td>
                  </tr>
                  <tr>
                    <td><strong>Save Manager</strong></td>
                    <td>Manual save/load/export</td>
                    <td>Exports JSON backup, loads from file, auto-saves every 30s</td>
                  </tr>
                </tbody>
              </table>

              <h4>Age Progress Bar</h4>
              <p>Visual indicator of your current era (1-11) with age name display. Changes color and appearance as you progress through eras.</p>

              <h4>Left Panel: Kingdom & Resources</h4>
              <ul>
                <li><strong>Kingdom Visual:</strong> Animated castle that evolves with your tier. Shows population count and total building count.</li>
                <li><strong>Resource Panel:</strong> All resources with real-time production rates
                  <ul>
                    <li><strong>Click icons:</strong> Manual gathering (4 base + upgrades, no cooldown, 5% crit chance for 2×)</li>
                    <li><strong>Hover "+X/sec":</strong> Opens detailed breakdown modal showing:
                      <ul>
                        <li>Base production from all buildings</li>
                        <li>Advisor bonuses & penalties (multiplicative)</li>
                        <li>Wonder effects (multiplicative)</li>
                        <li>Final production after all modifiers</li>
                      </ul>
                    </li>
                    <li><strong>Color coding:</strong> Positive production (green), negative (red), zero (gray)</li>
                  </ul>
                </li>
              </ul>

              <h4>Center Panel: Tabbed Content</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Tab</th><th>Content</th><th>Key Features</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>🗺️ World Map</strong></td>
                    <td>Hex-based exploration system</td>
                    <td>
                      • Pixi.js v8 rendering<br/>
                      • Click adjacent tiles to explore<br/>
                      • Auto-pathfinding with BFS algorithm<br/>
                      • Gathering (manual/auto)<br/>
                      • Tile building construction<br/>
                      • Expedition system
                    </td>
                  </tr>
                  <tr>
                    <td><strong>🏗️ Buildings</strong></td>
                    <td>Building management</td>
                    <td>
                      • Search/filter by name, resource, tier<br/>
                      • Purchase buildings (cost scales +20% per owned)<br/>
                      • Specialize buildings (⭐ +50% production)<br/>
                      • View building counts and total production<br/>
                      • Tier-based organization
                    </td>
                  </tr>
                  <tr>
                    <td><strong>🏛️ Wonders</strong></td>
                    <td>Wonder construction</td>
                    <td>
                      • Start/cancel wonder construction<br/>
                      • Timestamp-based progress (works offline)<br/>
                      • 50% refund on cancel<br/>
                      • Destroy completed wonders to free slots<br/>
                      • Shows ⏳ indicator when building<br/>
                      • Badge shows wonder count
                    </td>
                  </tr>
                </tbody>
              </table>

              <h4>Right Panel</h4>
              <table className="guide-table">
                <tbody>
                  <tr>
                    <td><strong>🔬 Tech Panel</strong></td>
                    <td>
                      • Double-click tech to research<br/>
                      • Queue (max 5), auto-advance<br/>
                      • Tree view (🌳): Click to pin details, double-click to research<br/>
                      • Hover shows preview (when unpinned)<br/>
                      • Click ✕ to unpin detail panel<br/>
                      • Tier filter, shows unlocks
                    </td>
                  </tr>
                  <tr><td><strong>📜 Quest Panel</strong></td><td>Active quest, objective tracking, auto-complete, 10 chapters, reward preview</td></tr>
                  <tr><td><strong>🔔 Notifications</strong></td><td>Bottom toasts: Success (green), Error (red), Warning (yellow), Info (blue)</td></tr>
                </tbody>
              </table>
            </>
          ),
        },
      },
    },
    'buildings': {
      name: 'Buildings',
      icon: '🏗️',
      topics: {
        'building-mechanics': {
          name: 'Building Mechanics',
          icon: '⚙️',
          content: (
            <>
              <h3>Building System</h3>

              <h4>Cost Formula</h4>
              <div className="guide-formula">
                <code>Cost = Base × 2.0 × (1.2 ^ Owned)</code>
              </div>
              <p>Example: Woodcutter (base 10) costs 20/24/29/35/42/51/61/73/88/103 wood for buildings 1-10.</p>

              <h4>Production Calculation</h4>
              <div className="guide-formula">
                <code>Total Production = Σ(Building Base Production) × Specialization × Advisors × Wonders</code>
              </div>

              <p><strong>Breakdown:</strong></p>
              <ol>
                <li><strong>Base Production Sum:</strong> Add up all buildings of all types producing this resource</li>
                <li><strong>Specialization Multiplier:</strong> 1.5× if this building type is specialized, else 1.0×</li>
                <li><strong>Advisor Multiplier:</strong> Product of all advisor bonuses/penalties for this resource</li>
                <li><strong>Wonder Multiplier:</strong> Product of all wonder bonuses for this resource + global bonuses</li>
              </ol>

              <h4>Building Categories & Association</h4>
              <p>Each building belongs to one category and produces 1-3 resources:</p>
              <table className="guide-table">
                <thead>
                  <tr><th>Category</th><th>Example Buildings</th><th>Primary Resources</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Wood & Nature</td>
                    <td>Woodcutter, Forester, Hunter</td>
                    <td>Wood, Food</td>
                  </tr>
                  <tr>
                    <td>Mining & Extraction</td>
                    <td>Quarry, Mine, Coal Mine, Gem Mine</td>
                    <td>Stone, Iron, Coal, Gems</td>
                  </tr>
                  <tr>
                    <td>Settlement & Agriculture</td>
                    <td>Farm, House, Granary, Windmill</td>
                    <td>Food, Population</td>
                  </tr>
                  <tr>
                    <td>Trade & Commerce</td>
                    <td>Market, Trading Post, Harbor, Bank</td>
                    <td>Gold</td>
                  </tr>
                  <tr>
                    <td>Cultural</td>
                    <td>Theater, Library, Museum</td>
                    <td>Culture, Knowledge</td>
                  </tr>
                  <tr>
                    <td>Industrial</td>
                    <td>Smelter, Factory, Workshop</td>
                    <td>Steel, Tools, Machinery</td>
                  </tr>
                  <tr>
                    <td>Scientific</td>
                    <td>Observatory, Laboratory, Research Facility</td>
                    <td>Science, Knowledge</td>
                  </tr>
                  <tr>
                    <td>Magical</td>
                    <td>Mage Tower, Enchantment Hall, Crystal Spire</td>
                    <td>Magic, Artifacts, Crystals</td>
                  </tr>
                </tbody>
              </table>

              <h4>Building Tiers (11 Total)</h4>
              <p>Buildings are unlocked by researching their required technology. Higher tiers provide exponentially more production:</p>
              <table className="guide-table">
                <thead>
                  <tr><th>Tier</th><th>Era</th><th>Production Scaling</th><th>Cost Scaling</th></tr>
                </thead>
                <tbody>
                  <tr><td>1</td><td>Stone Age</td><td>1-5/sec per building</td><td>10-50 resources</td></tr>
                  <tr><td>2</td><td>Bronze Age</td><td>3-10/sec</td><td>50-200 resources</td></tr>
                  <tr><td>3</td><td>Iron Age</td><td>8-25/sec</td><td>200-500 resources</td></tr>
                  <tr><td>4-5</td><td>Classical-Medieval</td><td>20-100/sec</td><td>500-2000 resources</td></tr>
                  <tr><td>6-7</td><td>Renaissance-Industrial</td><td>50-500/sec</td><td>2k-10k resources</td></tr>
                  <tr><td>8-9</td><td>Modern-Atomic</td><td>200-2000/sec</td><td>10k-50k resources</td></tr>
                  <tr><td>10-11</td><td>Information-Transcendence</td><td>1000-10000/sec</td><td>50k-500k resources</td></tr>
                </tbody>
              </table>

              <h4>Building Limits</h4>
              <ul>
                <li><strong>Per Type Maximum:</strong> 999 buildings (configurable in balance.js)</li>
                <li><strong>Total Buildings:</strong> Unlimited (no global cap)</li>
                <li><strong>Practical Limit:</strong> Performance and cost scaling naturally limit building spam</li>
              </ul>

              <h4>Special Building Mechanics</h4>

              <div className="guide-ability">
                <h4>Production Chain Buildings</h4>
                <p>Some buildings require multiple input resources to produce output:</p>
                <ul>
                  <li><strong>Smelter:</strong> Consumes iron + coal → Produces steel</li>
                  <li><strong>Workshop:</strong> Consumes iron + wood → Produces tools</li>
                  <li><strong>Factory:</strong> Consumes steel + tools → Produces machinery</li>
                </ul>
                <p><strong>Mechanic:</strong> Production rate is limited by the lowest input rate. If you produce 10 iron/sec but only 5 coal/sec, smelter is bottlenecked at 5 steel/sec.</p>
              </div>

              <div className="guide-ability">
                <h4>Population Growth Buildings</h4>
                <p>Houses, Cottages, Homesteads produce population over time:</p>
                <ul>
                  <li><strong>Base Rate:</strong> Defined in building data (e.g., 0.1 pop/sec)</li>
                  <li><strong>Food Requirement:</strong> Population growth halts if food production is negative</li>
                </ul>
              </div>
            </>
          ),
        },
        'specializations': {
          name: 'Specializations',
          icon: '⭐',
          content: (
            <>
              <h3>Building Specialization System</h3>

              <h4>What is Specialization?</h4>
              <p>A <strong>permanent upgrade</strong> that applies to ALL buildings of a specific type, increasing their production by <strong>+50%</strong> forever.</p>

              <h4>Specialization Mechanics</h4>
              <div className="guide-formula">
                <code>Specialized Production = Base Production × 1.5</code>
              </div>

              <p><strong>Key Points:</strong></p>
              <ul>
                <li>Applies to ALL existing and future buildings of that type</li>
                <li>One-time purchase per building type</li>
                <li>Cannot be undone or refunded</li>
                <li>Stacks multiplicatively with advisors and wonders</li>
                <li>Shows ⭐ indicator on specialized buildings</li>
              </ul>

              <h4>Specialization Cost Formula</h4>
              <div className="guide-formula">
                <code>Cost = Building Base Cost × Tier Multiplier × Production Value Factor</code>
              </div>

              <p><strong>Tier Multipliers:</strong></p>
              <table className="guide-table">
                <thead>
                  <tr><th>Tier</th><th>Cost Multiplier</th><th>Example Cost (Woodcutter)</th></tr>
                </thead>
                <tbody>
                  <tr><td>1</td><td>100×</td><td>1,000 wood</td></tr>
                  <tr><td>2</td><td>200×</td><td>Varies by building</td></tr>
                  <tr><td>3</td><td>400×</td><td>Varies by building</td></tr>
                  <tr><td>4-5</td><td>800-1,600×</td><td>High investment</td></tr>
                  <tr><td>6-7</td><td>3,200-6,400×</td><td>Strategic choice</td></tr>
                  <tr><td>8-11</td><td>12,800-100,000×</td><td>Late-game only</td></tr>
                </tbody>
              </table>

              <h4>When to Specialize</h4>

              <div className="guide-chain">
                <h4>Early Game (Tiers 1-3)</h4>
                <ul>
                  <li><strong>Prioritize:</strong> Buildings you have 10+ copies of</li>
                  <li><strong>Best Choices:</strong> Woodcutters, Farms, Quarries (foundation resources)</li>
                  <li><strong>Timing:</strong> After you have 10-15 of the building type</li>
                  <li><strong>ROI:</strong> Very fast (minutes to hours)</li>
                </ul>
              </div>

              <div className="guide-chain">
                <h4>Mid Game (Tiers 4-6)</h4>
                <ul>
                  <li><strong>Prioritize:</strong> Bottleneck resources</li>
                  <li><strong>Best Choices:</strong> Iron/gold/knowledge producers</li>
                  <li><strong>Timing:</strong> When you have steady production of the specialization cost resources</li>
                  <li><strong>ROI:</strong> Medium (hours to days)</li>
                </ul>
              </div>

              <div className="guide-chain">
                <h4>Late Game (Tiers 7-11)</h4>
                <ul>
                  <li><strong>Prioritize:</strong> High-tier resource producers you're scaling hard</li>
                  <li><strong>Best Choices:</strong> Science, Magic, Machinery, Crystals</li>
                  <li><strong>Timing:</strong> When you have 20+ buildings and excess resources</li>
                  <li><strong>ROI:</strong> Long (days) but massive impact</li>
                </ul>
              </div>

              <h4>Specialization Synergies</h4>

              <table className="guide-table">
                <thead>
                  <tr><th>Synergy Type</th><th>Effect</th><th>Example</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Advisor + Specialization</td>
                    <td>Multiplicative stacking</td>
                    <td>Merchant (+50% gold) + Specialized Markets = Base × 1.5 × 1.5 = 2.25× gold</td>
                  </tr>
                  <tr>
                    <td>Wonder + Specialization</td>
                    <td>Multiplicative stacking</td>
                    <td>Great Granary (+25% food) + Specialized Farms = Base × 1.5 × 1.25 = 1.875× food</td>
                  </tr>
                  <tr>
                    <td>Advisor + Wonder + Spec</td>
                    <td>Triple multiplicative</td>
                    <td>All three = Base × 1.5 × 1.5 × 1.25 = 2.8125× production!</td>
                  </tr>
                </tbody>
              </table>

              <h4>Strategic Specialization Order</h4>
              <p><strong>Recommended sequence for maximum efficiency:</strong></p>
              <ol>
                <li><strong>Woodcutters</strong> - Universal resource, always needed</li>
                <li><strong>Farms</strong> - Population growth is critical</li>
                <li><strong>Quarries/Stone Pits</strong> - Stone is always a bottleneck</li>
                <li><strong>Markets</strong> - Gold is used for everything</li>
                <li><strong>Libraries</strong> - Knowledge = research speed</li>
                <li><strong>Mines/Smelters</strong> - Iron/steel production chains</li>
                <li><strong>Late-game producers</strong> - Science, Magic, etc. as you reach those tiers</li>
              </ol>
            </>
          ),
        },
        'tile-buildings': {
          name: 'Tile Buildings',
          icon: '🗺️',
          content: (
            <>
              <h3>World Map Tile Building System</h3>

              <h4>What are Tile Buildings?</h4>
              <p>Buildings constructed directly on discovered World Map tiles. They function identically to regular buildings but cost 2× more and don't scale with owned count.</p>

              <h4>Tile Building Mechanics</h4>

              <div className="guide-formula">
                <code>Tile Building Cost = Base Building Cost × 2.0 (static, no scaling)</code>
              </div>

              <p><strong>Key Differences from Regular Buildings:</strong></p>
              <table className="guide-table">
                <thead>
                  <tr><th>Aspect</th><th>Regular Buildings</th><th>Tile Buildings</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Cost</td>
                    <td>Scales +20% per owned</td>
                    <td>Fixed 2× base cost</td>
                  </tr>
                  <tr>
                    <td>Location</td>
                    <td>Kingdom (abstract)</td>
                    <td>Specific map tile</td>
                  </tr>
                  <tr>
                    <td>Limit per tile</td>
                    <td>N/A</td>
                    <td>One building per tile</td>
                  </tr>
                  <tr>
                    <td>Biome restriction</td>
                    <td>None</td>
                    <td>Must match biome type</td>
                  </tr>
                  <tr>
                    <td>Specialization</td>
                    <td>Applies normally</td>
                    <td>Applies if building type specialized</td>
                  </tr>
                  <tr>
                    <td>Destruction</td>
                    <td>Can sell (future feature)</td>
                    <td>Cannot destroy (permanent)</td>
                  </tr>
                </tbody>
              </table>

              <h4>Biome Building Compatibility</h4>
              <p>Each biome allows specific building types based on thematic appropriateness:</p>

              <table className="guide-table">
                <thead>
                  <tr><th>Biome</th><th>Allowed Buildings</th><th>Strategy</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>🌲 Forest</td>
                    <td>Woodcutter, Forager, Hunter, Skin Trader</td>
                    <td>Wood production hub</td>
                  </tr>
                  <tr>
                    <td>🌾 Plains</td>
                    <td>Farm, Windmill, Granary, House, Cottage, Homestead, Barracks, Hospital</td>
                    <td>Food & population center</td>
                  </tr>
                  <tr>
                    <td>⛰️ Mountains</td>
                    <td>Stone Picker, Quarry, Prospector Camp, Mine, Deep Mine, Gold Mine, Gem Mine, Smelter, Coal Mine</td>
                    <td>Mining & extraction hub</td>
                  </tr>
                  <tr>
                    <td>🏖️ Coastal</td>
                    <td>Market, Trading Post, Harbor, Bank</td>
                    <td>Gold generation focus</td>
                  </tr>
                  <tr>
                    <td>🏜️ Desert</td>
                    <td>Trading Post, Market, Gem Mine</td>
                    <td>Trade & gems</td>
                  </tr>
                  <tr>
                    <td>🌿 Swamp</td>
                    <td>Forager, Hunter, Herbalist</td>
                    <td>Specialized foraging</td>
                  </tr>
                  <tr>
                    <td>❄️ Tundra</td>
                    <td>Mine, Coal Mine, Smelter</td>
                    <td>Industrial resources</td>
                  </tr>
                </tbody>
              </table>

              <h4>How to Build on Tiles</h4>
              <ol>
                <li><strong>Explore & Discover:</strong> Move to a new tile to discover it</li>
                <li><strong>Select Tile:</strong> Click on a discovered tile on the World Map</li>
                <li><strong>Open Build Menu:</strong> Click "Build on Tile" button in tile preview panel</li>
                <li><strong>Choose Building:</strong> Select from biome-compatible buildings list</li>
                <li><strong>Confirm:</strong> Pay 2× base cost to construct</li>
                <li><strong>Indicator:</strong> Tile shows 🏠 icon to indicate building present</li>
              </ol>

              <h4>Tile Building Strategy</h4>

              <div className="guide-chain">
                <h4>Early Game (First 20 tiles)</h4>
                <ul>
                  <li><strong>Don't build yet:</strong> Tile buildings are expensive (2× cost) with no scaling</li>
                  <li><strong>Focus on regular buildings:</strong> Better ROI with scaling costs</li>
                  <li><strong>Exception:</strong> If you find a very high-quality tile (120-130%) and have excess resources</li>
                </ul>
              </div>

              <div className="guide-chain">
                <h4>Mid Game (20-100 tiles)</h4>
                <ul>
                  <li><strong>Start selectively:</strong> Build on highest quality tiles first</li>
                  <li><strong>Prioritize biomes:</strong> Match your bottleneck resources</li>
                  <li><strong>Strategic placement:</strong> Match building types to biome resources for maximum synergy</li>
                </ul>
              </div>

              <div className="guide-chain">
                <h4>Late Game (100+ tiles)</h4>
                <ul>
                  <li><strong>Fill high-quality tiles:</strong> Every 120%+ tile should have a building</li>
                  <li><strong>Specialize then build:</strong> Specialize building types BEFORE placing on tiles for immediate +50%</li>
                  <li><strong>Strategic placement:</strong> Place high-tier buildings on best tiles for maximum impact</li>
                </ul>
              </div>

              <h4>Tile Quality Impact</h4>
              <p>Tile quality affects GATHERING yield, not building production. Buildings on tiles produce at their standard rate regardless of tile quality.</p>

              <h4>Advanced Optimization</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Optimization</th><th>Method</th><th>Impact</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Specialization Timing</td>
                    <td>Specialize building type BEFORE placing on tiles</td>
                    <td>Immediate +50% production</td>
                  </tr>
                  <tr>
                    <td>Biome Clustering</td>
                    <td>Build same building type on multiple tiles of same biome</td>
                    <td>Easier to track, consistent production</td>
                  </tr>
                  <tr>
                    <td>Late-game scaling</td>
                    <td>Use tile buildings to avoid regular building cost scaling</td>
                    <td>200+ buildings would be very expensive - tile buildings stay fixed cost</td>
                  </tr>
                </tbody>
              </table>
            </>
          ),
        },
      },
    },
    'world-map': {
      name: 'World Map',
      icon: '🗺️',
      topics: {
        'exploration-system': {
          name: 'Exploration System',
          icon: '🧭',
          content: (
            <>
              <h3>World Map Exploration Mechanics</h3>

              <h4>Hex Grid System</h4>
              <p>The World Map uses <strong>axial coordinates</strong> for hexagonal tiles:</p>
              <div className="guide-formula">
                <code>Node ID = "node_" + q + "_" + r</code>
              </div>

              <p><strong>Coordinate System:</strong></p>
              <ul>
                <li><strong>Origin (Kingdom):</strong> q=0, r=0 (coordinates 0, 0)</li>
                <li><strong>Adjacent tiles:</strong> 6 directions from any tile (NE, E, SE, SW, W, NW)</li>
                <li><strong>Distance formula:</strong> Manhattan distance in hex space</li>
              </ul>

              <h4>Movement Mechanics</h4>

              <table className="guide-table">
                <thead>
                  <tr><th>Mechanic</th><th>Value</th><th>Modifiable By</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Base Cooldown</td>
                    <td>10 seconds</td>
                    <td>Technologies (reducible to ~3-5 seconds)</td>
                  </tr>
                  <tr>
                    <td>Adjacency Requirement</td>
                    <td>Must click adjacent tile</td>
                    <td>Auto-pathfinding can navigate multiple tiles</td>
                  </tr>
                  <tr>
                    <td>Exploration Limit</td>
                    <td>Base 30 tiles</td>
                    <td>Technologies (expandable to 200+ tiles)</td>
                  </tr>
                  <tr>
                    <td>Teleport Beacons</td>
                    <td>Instant travel, no cooldown</td>
                    <td>None (always instant)</td>
                  </tr>
                  <tr>
                    <td>Kingdom Return</td>
                    <td>Instant teleport home</td>
                    <td>None (always available)</td>
                  </tr>
                </tbody>
              </table>

              <h4>Exploration Limit System</h4>
              <p>You can only discover a limited number of tiles before needing to research technologies to expand:</p>

              <div className="guide-formula">
                <code>Max Tiles = Base Limit (30) + Σ(Tech Bonuses)</code>
              </div>

              <p><strong>Tech Expansion Bonuses:</strong></p>
              <ul>
                <li>Early techs: +5 to +10 tiles each</li>
                <li>Mid techs: +15 to +25 tiles each</li>
                <li>Late techs: +30 to +50 tiles each</li>
                <li>Total possible: 300+ tiles by end-game</li>
              </ul>

              <h4>Auto-Pathfinding Algorithm</h4>
              <p>Click any discovered tile (even non-adjacent) to auto-navigate:</p>

              <ol>
                <li><strong>BFS Pathfinding:</strong> Breadth-First Search finds shortest path</li>
                <li><strong>Gap Handling:</strong> Can navigate around single undiscovered tiles</li>
                <li><strong>Movement Queue:</strong> Auto-moves every 10 seconds (or current cooldown)</li>
                <li><strong>Path Preview:</strong> Shows numbered steps with movement cost indicators</li>
                <li><strong>Cancellation:</strong> Click anywhere to cancel auto-move</li>
              </ol>

              <p><strong>Pathfinding Costs:</strong></p>
              <table className="guide-table">
                <thead>
                  <tr><th>Tile Type</th><th>Movement Cost</th><th>Visual Indicator</th></tr>
                </thead>
                <tbody>
                  <tr><td>Discovered tile</td><td>1 move (10s)</td><td>Green path segment</td></tr>
                  <tr><td>Undiscovered tile</td><td>Cannot path through</td><td>N/A</td></tr>
                  <tr><td>Beacon tile</td><td>Instant teleport</td><td>Blue teleport line</td></tr>
                  <tr><td>Kingdom tile</td><td>Instant teleport</td><td>Gold return button</td></tr>
                </tbody>
              </table>

              <h4>Starter Protection System</h4>
              <p>The first 10 tiles guarantee access to essential resources:</p>

              <div className="guide-formula">
                <code>Guaranteed Resources: Wood, Stone, Food, Knowledge</code>
              </div>

              <p><strong>Protection Mechanism:</strong></p>
              <ul>
                <li><strong>Tiles 1-6:</strong> Weighted random from starter biomes (Forest 30%, Plains 25%, Mountains 20%, etc.)</li>
                <li><strong>Tile 7:</strong> Force-spawn check - if missing knowledge, spawn Overgrown Shrine (🗿)</li>
                <li><strong>Tile 7+:</strong> Continue force-spawn checks for any missing essentials (wood/stone/food/knowledge)</li>
                <li><strong>Tile 11+:</strong> Protection ends, normal biome generation</li>
              </ul>

              <p><strong>Guaranteed Biomes:</strong></p>
              <table className="guide-table">
                <thead>
                  <tr><th>Resource</th><th>Biomes</th><th>Force-Spawn Tile</th></tr>
                </thead>
                <tbody>
                  <tr><td>🌲 Wood</td><td>Forest, Swamp</td><td>By tile 10</td></tr>
                  <tr><td>🗻 Stone</td><td>Mountains, Rocky Outcrop</td><td>By tile 10</td></tr>
                  <tr><td>🌾 Food</td><td>Plains, Grassland, Forest, Swamp</td><td>By tile 10</td></tr>
                  <tr><td>🗿 Knowledge</td><td>Overgrown Shrine</td><td>By tile 7</td></tr>
                </tbody>
              </table>

              <h4>Tile Discovery Events</h4>
              <p>When discovering a new tile, a random entry event triggers based on biome:</p>

              <p><strong>Event Types:</strong></p>
              <ul>
                <li><strong>Peaceful (40-50% weight):</strong> Flavor text, no effect</li>
                <li><strong>Positive (30-40% weight):</strong> Gain resources (5-20 depending on biome)</li>
                <li><strong>Negative (10-20% weight):</strong> Lose small amount of resources (3-10)</li>
                <li><strong>Rare (5-10% weight):</strong> Large resource gain or special effects</li>
              </ul>

              <p><strong>Event Example (Forest Biome):</strong></p>
              <table className="guide-table">
                <thead>
                  <tr><th>Event</th><th>Weight</th><th>Effect</th></tr>
                </thead>
                <tbody>
                  <tr><td>Peaceful</td><td>50%</td><td>"The forest is peaceful. Birds sing in the canopy above."</td></tr>
                  <tr><td>Berries</td><td>20%</td><td>+5 food</td></tr>
                  <tr><td>Herbs</td><td>15%</td><td>+3 food</td></tr>
                  <tr><td>Old Cache</td><td>10%</td><td>+10 wood, +5 food</td></tr>
                  <tr><td>Wolves</td><td>5%</td><td>-3 food (penalty)</td></tr>
                </tbody>
              </table>

              <h4>Tile Quality System</h4>
              <p>Each tile has a deterministic quality multiplier for gathering:</p>

              <div className="guide-formula">
                <code>Quality = 0.7 + (0.6 × abs(sin(q × 12.9898 + r × 78.233)))</code>
              </div>

              <p><strong>Quality Range:</strong> 70% to 130% (0.7 to 1.3 multiplier)</p>

              <p><strong>Properties:</strong></p>
              <ul>
                <li><strong>Deterministic:</strong> Same coordinates always have same quality</li>
                <li><strong>Visual Indicator:</strong> Slightly brighter/darker hex color</li>
                <li><strong>Affects:</strong> Gathering yield only (NOT building production on tiles)</li>
                <li><strong>Stacks with:</strong> Age bonuses, tile building production</li>
              </ul>

              <h4>Beacon Pity Timer</h4>
              <p>Teleport Beacons (🔮) spawn via pity timer after starter protection ends:</p>

              <ul>
                <li><strong>Counter:</strong> Increments by 1 for each non-beacon tile discovered after tile 10</li>
                <li><strong>Spawn Chance:</strong> Increases exponentially with counter</li>
                <li><strong>Guaranteed Spawn:</strong> After 30-50 tiles without a beacon</li>
                <li><strong>Reset:</strong> Counter resets to 0 when beacon spawns</li>
              </ul>
            </>
          ),
        },
        'gathering-system': {
          name: 'Gathering System',
          icon: '⛏️',
          content: (
            <>
              <h3>Resource Gathering Mechanics</h3>

              <h4>Manual Gathering (Spam-Friendly)</h4>
              <p>Click the ⛏️ Gather button on any discovered tile:</p>

              <table className="guide-table">
                <thead>
                  <tr><th>Property</th><th>Value</th><th>Details</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Cooldown</td>
                    <td>None (0ms)</td>
                    <td>Spam-clickable for fast gathering</td>
                  </tr>
                  <tr>
                    <td>Animation</td>
                    <td>0.2s bounce</td>
                    <td>Scale animation: 1.0 → 1.12 → 0.95 → 1.0</td>
                  </tr>
                  <tr>
                    <td>Button State</td>
                    <td>Primary (green)</td>
                    <td>Changes to blue when auto-gather active</td>
                  </tr>
                  <tr>
                    <td>Notification</td>
                    <td>Chat-style toast</td>
                    <td>Shows "+X resource" at top center, fades after 1.5s</td>
                  </tr>
                </tbody>
              </table>

              <h4>Auto-Gather System</h4>
              <p>Toggle button (▶️ play / ⏸️ pause) enables automated gathering:</p>

              <table className="guide-table">
                <thead>
                  <tr><th>Property</th><th>Value</th><th>Details</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Interval</td>
                    <td>0.5 seconds</td>
                    <td>Configurable in balance.js: GATHER.autoGatherDelay</td>
                  </tr>
                  <tr>
                    <td>Background Support</td>
                    <td>Yes (Page Visibility API)</td>
                    <td>Continues gathering even when tab is inactive</td>
                  </tr>
                  <tr>
                    <td>Catch-Up Mechanic</td>
                    <td>Max 10 missed gathers</td>
                    <td>When returning to tab, applies up to 10 gathers instantly</td>
                  </tr>
                  <tr>
                    <td>Expedition Block</td>
                    <td>Cannot gather during expedition</td>
                    <td>Auto-gather pauses, resumes when expedition completes</td>
                  </tr>
                  <tr>
                    <td>Button Color</td>
                    <td>Blue when active</td>
                    <td>Visual indicator that auto-gather is running</td>
                  </tr>
                </tbody>
              </table>

              <h4>Gather Yield Formula</h4>
              <div className="guide-formula">
                <code>Yield = Base Amount × Tile Quality × Age Multiplier</code>
              </div>

              <p><strong>Component Breakdown:</strong></p>
              <table className="guide-table">
                <thead>
                  <tr><th>Component</th><th>Formula</th><th>Example</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Base Amount</td>
                    <td>Defined in biome data</td>
                    <td>Forest: 5 wood, 3 food</td>
                  </tr>
                  <tr>
                    <td>Tile Quality</td>
                    <td>0.7 to 1.3 (deterministic)</td>
                    <td>110% quality = 1.1× multiplier</td>
                  </tr>
                  <tr>
                    <td>Age Multiplier</td>
                    <td>1.0 + (era × 0.05)</td>
                    <td>Era 5: 1.0 + (5 × 0.05) = 1.25×</td>
                  </tr>
                </tbody>
              </table>

              <h4>Example Calculation</h4>
              <p><strong>Scenario:</strong> Gathering from Forest tile in Era 5</p>

              <ul>
                <li><strong>Base Amount:</strong> 5 wood</li>
                <li><strong>Tile Quality:</strong> 120% (1.2×)</li>
                <li><strong>Age Multiplier:</strong> Era 5 = 1.25×</li>
              </ul>

              <p><strong>Calculation:</strong></p>
              <div className="guide-formula">
                <code>Wood Gathered = 5 × 1.2 × 1.25 = 7.5 wood per gather</code>
              </div>

              <p>With auto-gather (0.5s interval), this tile produces <strong>15 wood/second</strong> from gathering alone!</p>

              <h4>Multi-Resource Gathering</h4>
              <p>Some biomes provide multiple resources per gather:</p>

              <table className="guide-table">
                <thead>
                  <tr><th>Biome</th><th>Resources</th><th>Richness Factor</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Forest</td>
                    <td>5 wood (1.0×), 3 food (0.6×)</td>
                    <td>Wood is primary, food is secondary</td>
                  </tr>
                  <tr>
                    <td>Mountains</td>
                    <td>4 stone (1.0×), 2 iron (0.7×)</td>
                    <td>Both minerals, iron slightly lower</td>
                  </tr>
                  <tr>
                    <td>Coastal</td>
                    <td>3 gold (1.0×), 2 food (0.5×)</td>
                    <td>Trade focus with fishing</td>
                  </tr>
                </tbody>
              </table>

              <p><strong>Richness Factor:</strong> Secondary resources have lower base amounts but still benefit from all multipliers.</p>

              <h4>Gathering vs Building Production</h4>
              <p>How does gathering compare to building production?</p>

              <table className="guide-table">
                <thead>
                  <tr><th>Source</th><th>Early Game</th><th>Mid Game</th><th>Late Game</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Manual Gathering</td>
                    <td>Very strong (5-10/sec spam clicking)</td>
                    <td>Moderate (buildings overtake)</td>
                    <td>Weak (negligible vs 1000s/sec from buildings)</td>
                  </tr>
                  <tr>
                    <td>Auto-Gather</td>
                    <td>Strong (8-16/sec from good tiles)</td>
                    <td>Good supplement (100-300/sec total)</td>
                    <td>Passive income (500-1000/sec total from many tiles)</td>
                  </tr>
                  <tr>
                    <td>Buildings</td>
                    <td>Weak (5-10/sec total)</td>
                    <td>Strong (100-500/sec total)</td>
                    <td>Dominant (10,000-100,000/sec total)</td>
                  </tr>
                </tbody>
              </table>

              <h4>Optimization Tips</h4>
              <ol>
                <li><strong>Early Game:</strong> Spam click gathering for bursts of resources while waiting for buildings</li>
                <li><strong>Mid Game:</strong> Enable auto-gather on 5-10 high-quality tiles for passive income</li>
                <li><strong>Late Game:</strong> Auto-gather on 50+ tiles provides significant passive supplement</li>
                <li><strong>Focus Quality:</strong> Prioritize 110%+ tiles for maximum efficiency</li>
                <li><strong>Age Up:</strong> Each era gives +5% gathering, Era 10 = +50% gathering!</li>
              </ol>
            </>
          ),
        },
        'expedition-system': {
          name: 'Expedition System',
          icon: '🏕️',
          content: (
            <>
              <h3>Expeditions</h3>

              <h4>Formula & Durations</h4>
              <div className="guide-formula">
                <code>Yield = (Base × Quality × Age) × Multiplier</code>
              </div>
              <table className="guide-table">
                <thead><tr><th>Duration</th><th>×Mult</th><th>Use Case</th></tr></thead>
                <tbody>
                  <tr><td>30s</td><td>×20</td><td>Quick test</td></tr>
                  <tr><td>1m</td><td>×40</td><td>Bio break</td></tr>
                  <tr><td>2m</td><td>×100</td><td>Meal prep</td></tr>
                  <tr><td>5m</td><td>×320</td><td>Cooking</td></tr>
                  <tr><td>10m</td><td>×550</td><td>Extended AFK</td></tr>
                </tbody>
              </table>

              <h4>Rules & Optimization</h4>
              <ul>
                <li><strong>⚠️ All-or-Nothing:</strong> Cancel = ZERO rewards. Timestamp-based, works offline.</li>
                <li><strong>🔒 Locked:</strong> Cannot move/teleport/gather during expedition.</li>
                <li><strong>Best Tiles:</strong> 120-130% quality (+20-30% yield). Target bottleneck resources.</li>
                <li><strong>Late Game:</strong> Era 10 + 130% tile + ×550 = 4,000-5,000+ resources per 10m.</li>
              </ul>

              <h4>Comparison</h4>
              <table className="guide-table">
                <thead><tr><th>Method</th><th>Yield (Forest)</th><th>Best For</th></tr></thead>
                <tbody>
                  <tr><td>Manual Click</td><td>7.5/click</td><td>Burst</td></tr>
                  <tr><td>Auto-Gather</td><td>15/sec</td><td>Active play</td></tr>
                  <tr><td>Expedition 2m</td><td>750 (×100)</td><td>Short AFK</td></tr>
                  <tr><td>Expedition 10m</td><td>4,125 (×550)</td><td>Max AFK</td></tr>
                </tbody>
              </table>
            </>
          ),
        },
      },
    },
    'character-dungeons': {
      name: 'Character & Dungeons',
      icon: '⚔️',
      topics: {
        'character-overview': {
          name: 'Character Overview',
          icon: '🧙‍♂️',
          content: (
            <>
              <h3>Your Character System</h3>
              <p>Your character is the hero who explores dungeons, fights monsters, and grows stronger through equipment and progression.</p>

              <div className="guide-ability">
                <h4>🎯 Key Features</h4>
                <ul>
                  <li><strong>Level System:</strong> Gain experience from defeating dungeon enemies</li>
                  <li><strong>Equipment Slots:</strong> Equip weapons, armor, and accessories for stat bonuses</li>
                  <li><strong>Inventory Management:</strong> Store and organize items, materials, and equipment</li>
                  <li><strong>Combat Stats:</strong> View your offensive and defensive capabilities</li>
                </ul>
              </div>

              <div className="guide-chain">
                <p>💡 <strong>Tip:</strong> Your character's power directly affects your success in dungeons. Better equipment = stronger character!</p>
              </div>

              <h4>Primary Stats System</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Stat</th><th>Icon</th><th>Effects</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Strength (STR)</strong></td>
                    <td>💪</td>
                    <td>Increases HP (+5), Attack (+1.5), and Defense (+0.5) per point</td>
                  </tr>
                  <tr>
                    <td><strong>Dexterity (DEX)</strong></td>
                    <td>🎯</td>
                    <td>Increases Attack (+0.5), Accuracy (+0.5%), Crit Chance (+0.3%), Evasion (+0.3%), Attack Speed (+0.01×) per point</td>
                  </tr>
                  <tr>
                    <td><strong>Intelligence (INT)</strong></td>
                    <td>🧠</td>
                    <td>Increases HP (+2), Defense (+0.8), Accuracy (+0.3%), Gold Find (+0.3%), XP Gain (+0.4%) per point</td>
                  </tr>
                  <tr>
                    <td><strong>Luck (LUK)</strong></td>
                    <td>🍀</td>
                    <td>Increases Crit Chance (+0.4%), Crit Damage (+1%), Evasion (+0.2%), Gold Find (+0.5%) per point</td>
                  </tr>
                </tbody>
              </table>

              <h4>Stat Points</h4>
              <p>You earn stat points when leveling up. Allocate them strategically:</p>
              <ul>
                <li><strong>Tank Build:</strong> Focus STR for high HP and defense</li>
                <li><strong>DPS Build:</strong> Focus DEX and LUK for high crit damage</li>
                <li><strong>Balanced Build:</strong> Mix of all stats for versatility</li>
                <li><strong>Farmer Build:</strong> INT and LUK for gold and XP bonuses</li>
              </ul>
            </>
          ),
        },
        'equipment-stats': {
          name: 'Equipment & Stats',
          icon: '🛡️',
          content: (
            <>
              <h3>Equipment System</h3>
              <p>Equipment provides stat bonuses that enhance your character's combat abilities.</p>

              <h4>Equipment Slots (12 Total)</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Slot</th><th>Typical Stats</th><th>Purpose</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>⚔️ Weapon</strong></td>
                    <td>Attack, Crit Chance</td>
                    <td>Primary damage source</td>
                  </tr>
                  <tr>
                    <td><strong>🛡️ Offhand</strong></td>
                    <td>Defense, Health</td>
                    <td>Shield or secondary weapon</td>
                  </tr>
                  <tr>
                    <td><strong>⛑️ Helmet</strong></td>
                    <td>Defense, Health</td>
                    <td>Head protection</td>
                  </tr>
                  <tr>
                    <td><strong>🦸 Cape</strong></td>
                    <td>Evasion, Speed</td>
                    <td>Mobility and dodge</td>
                  </tr>
                  <tr>
                    <td><strong>👕 Top</strong></td>
                    <td>Defense, Health</td>
                    <td>Body armor</td>
                  </tr>
                  <tr>
                    <td><strong>👖 Bottom</strong></td>
                    <td>Defense, Health</td>
                    <td>Leg armor</td>
                  </tr>
                  <tr>
                    <td><strong>🧤 Gloves</strong></td>
                    <td>Attack, Attack Speed</td>
                    <td>Hand protection and dexterity</td>
                  </tr>
                  <tr>
                    <td><strong>👢 Boots</strong></td>
                    <td>Speed, Evasion</td>
                    <td>Movement and mobility</td>
                  </tr>
                  <tr>
                    <td><strong>⚙️ Belt</strong></td>
                    <td>Health, Defense</td>
                    <td>Utility slot</td>
                  </tr>
                  <tr>
                    <td><strong>💎 Pendant</strong></td>
                    <td>Various bonuses</td>
                    <td>Specialized effects (1 slot)</td>
                  </tr>
                  <tr>
                    <td><strong>💍 Ring (×2)</strong></td>
                    <td>Various bonuses</td>
                    <td>Specialized effects (2 slots)</td>
                  </tr>
                </tbody>
              </table>

              <h4>How to Equip Items</h4>
              <ol>
                <li><strong>Drag & Drop:</strong> Drag equipment from your inventory to the equipment slot</li>
                <li><strong>Double-Click:</strong> Double-click equipment in inventory to auto-equip</li>
                <li><strong>Right-Click:</strong> Right-click an item in your inventory for a context menu</li>
                <li><strong>Unequip:</strong> Click equipped item to move it back to inventory</li>
              </ol>

              <h4>Understanding Combat Stats</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Stat</th><th>Formula</th><th>What It Does</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>❤️ HP</strong></td>
                    <td>100 + (STR × 5) + (INT × 2) + Equipment</td>
                    <td>Your maximum health in combat</td>
                  </tr>
                  <tr>
                    <td><strong>⚔️ Attack</strong></td>
                    <td>10 + (STR × 1.5) + (DEX × 0.5) + Equipment</td>
                    <td>Base damage dealt per hit</td>
                  </tr>
                  <tr>
                    <td><strong>🛡️ Defense</strong></td>
                    <td>5 + (STR × 0.5) + (INT × 0.8) + Equipment</td>
                    <td>Reduces incoming damage</td>
                  </tr>
                  <tr>
                    <td><strong>🎯 Accuracy</strong></td>
                    <td>80% + (DEX × 0.5%) + (INT × 0.3%) + Equipment</td>
                    <td>Chance to hit enemies</td>
                  </tr>
                  <tr>
                    <td><strong>🌪️ Evasion</strong></td>
                    <td>5% + (DEX × 0.3%) + (LUK × 0.2%) + Equipment</td>
                    <td>Chance to dodge attacks</td>
                  </tr>
                  <tr>
                    <td><strong>💥 Crit Chance</strong></td>
                    <td>5% + (DEX × 0.3%) + (LUK × 0.4%) + Equipment</td>
                    <td>Chance to deal critical damage</td>
                  </tr>
                  <tr>
                    <td><strong>⚡ Crit Damage</strong></td>
                    <td>150% + (LUK × 1%) + Equipment</td>
                    <td>Damage multiplier on crits</td>
                  </tr>
                  <tr>
                    <td><strong>⚡ Attack Speed</strong></td>
                    <td>1.0× + (DEX × 0.01×) + Equipment</td>
                    <td>Attack frequency multiplier</td>
                  </tr>
                  <tr>
                    <td><strong>💰 Gold Find</strong></td>
                    <td>(LUK × 0.5%) + (INT × 0.3%) + Equipment</td>
                    <td>Bonus gold from all sources</td>
                  </tr>
                  <tr>
                    <td><strong>⭐ XP Gain</strong></td>
                    <td>100% + (INT × 0.4%) + Equipment</td>
                    <td>Bonus experience from combat</td>
                  </tr>
                </tbody>
              </table>

              <h4>Equipment Quality System</h4>
              <p>Equipment has quality modifiers that scale stats:</p>
              <div className="guide-formula">
                <code>Stat Value = Base Stat × (Quality / 50)</code>
              </div>
              <ul>
                <li><strong>50% Quality:</strong> Base stats (1.0×)</li>
                <li><strong>75% Quality:</strong> 1.5× stats</li>
                <li><strong>100% Quality:</strong> 2.0× stats</li>
                <li><strong>125% Quality:</strong> 2.5× stats</li>
              </ul>
            </>
          ),
        },
        'inventory-management': {
          name: 'Inventory System',
          icon: '🎒',
          content: (
            <>
              <h3>Inventory Management</h3>
              <p>The inventory stores all items you collect from dungeons, including equipment, consumables, and materials.</p>

              <h4>📦 Inventory Features</h4>
              <ul>
                <li><strong>Grid Layout:</strong> Items are organized in a 5×10 visual grid (50 slots per tab)</li>
                <li><strong>Drag & Drop:</strong> Reorganize items by dragging them to new positions</li>
                <li><strong>Stacking:</strong> Materials and consumables stack automatically (e.g., "Bone ×25")</li>
                <li><strong>Equipment:</strong> Each piece of equipment takes one inventory slot</li>
                <li><strong>Tooltips:</strong> Hover over items to see detailed information</li>
                <li><strong>Context Menu:</strong> Right-click items for quick actions</li>
              </ul>

              <h4>Inventory Tabs</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Tab</th><th>Icon</th><th>Contains</th><th>Max Items</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Equipment</strong></td>
                    <td>⚔️</td>
                    <td>Weapons, armor, accessories</td>
                    <td>50 items</td>
                  </tr>
                  <tr>
                    <td><strong>Consumables</strong></td>
                    <td>🧪</td>
                    <td>Potions, food, buff items</td>
                    <td>50 stacks</td>
                  </tr>
                  <tr>
                    <td><strong>Misc</strong></td>
                    <td>📦</td>
                    <td>Materials, quest items, dungeon resources</td>
                    <td>50 stacks</td>
                  </tr>
                  <tr>
                    <td><strong>Backpack</strong></td>
                    <td>🎒</td>
                    <td>Overflow storage when tabs are full</td>
                    <td>Unlimited</td>
                  </tr>
                </tbody>
              </table>

              <h4>Item Types</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Type</th><th>Icon Examples</th><th>Stacks?</th><th>Description</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Equipment</strong></td>
                    <td>⚔️ 🛡️ 💍</td>
                    <td>No</td>
                    <td>Weapons, armor, accessories - each has unique stats</td>
                  </tr>
                  <tr>
                    <td><strong>Materials</strong></td>
                    <td>🦴 🧳 ✨</td>
                    <td>Yes</td>
                    <td>Dungeon resources used for crafting and research</td>
                  </tr>
                  <tr>
                    <td><strong>Consumables</strong></td>
                    <td>🧪 📿</td>
                    <td>Yes</td>
                    <td>Potions and temporary buffs</td>
                  </tr>
                </tbody>
              </table>

              <div className="guide-chain">
                <p>💡 <strong>Tip:</strong> Materials like Bone (🦴) are used in certain technology research! Keep them in your inventory.</p>
              </div>

              <h4>Inventory Actions</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Action</th><th>How To</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Equip Item</strong></td>
                    <td>Double-click equipment OR drag to equipment slot</td>
                  </tr>
                  <tr>
                    <td><strong>Use Consumable</strong></td>
                    <td>Double-click potion or food item</td>
                  </tr>
                  <tr>
                    <td><strong>Delete Items</strong></td>
                    <td>Click 🗑️ Delete button, select items, confirm deletion</td>
                  </tr>
                  <tr>
                    <td><strong>Drag to Delete</strong></td>
                    <td>Drag item to trash zone (🗑️) in top-right</td>
                  </tr>
                  <tr>
                    <td><strong>Sort Inventory</strong></td>
                    <td>Use dropdown: Default, Rarity, Type, or Name</td>
                  </tr>
                  <tr>
                    <td><strong>View Item Info</strong></td>
                    <td>Ctrl+Click item for detailed modal</td>
                  </tr>
                  <tr>
                    <td><strong>Select Multiple</strong></td>
                    <td>Shift+Click or right-click items in delete mode</td>
                  </tr>
                </tbody>
              </table>

              <h4>Inventory Management Tips</h4>
              <ul>
                <li><strong>Sort by Rarity:</strong> Quickly find your best equipment</li>
                <li><strong>Delete Common Items:</strong> Make space by removing low-tier equipment</li>
                <li><strong>Stack Materials:</strong> Materials auto-stack, so you can hold thousands</li>
                <li><strong>Use Backpack:</strong> Overflow goes to backpack - clean it regularly</li>
                <li><strong>Dungeon Materials:</strong> Cannot be deleted, managed in Resources tab</li>
              </ul>
            </>
          ),
        },
        'dungeons-progression': {
          name: 'Dungeons & Progression',
          icon: '🏰',
          content: (
            <>
              <h3>Dungeon System</h3>
              <p>Dungeons are special tiles on the World Map that contain enemies, treasure, and valuable materials.</p>

              <h4>🗺️ Finding Dungeons</h4>
              <ul>
                <li><strong>Location:</strong> Dungeons appear as special biomes on the World Map</li>
                <li><strong>Visual:</strong> Highlighted with a <span style={{color: '#a060d0'}}>purple border</span> when discovered</li>
                <li><strong>Tiers:</strong> Dungeons have difficulty tiers (T1, T2, T3) matching your progression</li>
                <li><strong>Entry:</strong> Click on a dungeon tile and select "Enter Dungeon"</li>
                <li><strong>Guaranteed Spawns:</strong> Age-appropriate dungeon spawns every 15 tiles (pity timer)</li>
              </ul>

              <h4>Dungeon Tiers</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Tier</th><th>Name</th><th>Age</th><th>Recommended Level</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>T1</strong></td>
                    <td>Ancient Ruins</td>
                    <td>Age 1-2</td>
                    <td>Level 1-5</td>
                  </tr>
                  <tr>
                    <td><strong>T2</strong></td>
                    <td>Dark Catacombs</td>
                    <td>Age 3-4</td>
                    <td>Level 6-10</td>
                  </tr>
                  <tr>
                    <td><strong>T3</strong></td>
                    <td>Corrupted Temple</td>
                    <td>Age 5+</td>
                    <td>Level 11+</td>
                  </tr>
                </tbody>
              </table>

              <h4>Dungeon Rewards</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Reward Type</th><th>Where to Find</th><th>Use</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Equipment</strong></td>
                    <td>Enemy drops, treasure chests</td>
                    <td>Equip to increase stats</td>
                  </tr>
                  <tr>
                    <td><strong>Materials</strong></td>
                    <td>Enemy drops, boss rewards</td>
                    <td>Technology research, crafting</td>
                  </tr>
                  <tr>
                    <td><strong>Experience</strong></td>
                    <td>Defeating enemies</td>
                    <td>Level up your character</td>
                  </tr>
                  <tr>
                    <td><strong>Gold</strong></td>
                    <td>Treasure chests, boss drops</td>
                    <td>Kingdom economy</td>
                  </tr>
                </tbody>
              </table>

              <h4>Dungeon Materials & Research</h4>
              <p>Some materials are <strong>only found in dungeons</strong> and are required for advanced technologies:</p>

              <div className="guide-chain">
                <p><strong>Example:</strong> The "Necromancy" technology (Era 2) requires <strong>25 Bone 🦴</strong> from dungeons.</p>
                <p>Without dungeon materials, you cannot progress past certain eras!</p>
              </div>

              <h4>📊 Tracking Dungeon Materials</h4>
              <p>Dungeon materials can be viewed in two places:</p>
              <ul>
                <li><strong>Resource Panel:</strong> Shows "Dungeon" category with all dungeon materials and quantities</li>
                <li><strong>Character Inventory:</strong> Materials appear as stackable items (e.g., "Bone ×25") in the Misc tab</li>
              </ul>

              <h4>Dungeon Layout</h4>
              <p>Dungeons use a hex-based exploration system similar to the World Map:</p>
              <ul>
                <li><strong>Entrance Tile:</strong> Safe starting point, can rest to restore HP</li>
                <li><strong>Corridor Tiles:</strong> Empty passages for navigation</li>
                <li><strong>Enemy Tiles:</strong> Contains monsters to fight</li>
                <li><strong>Trap Tiles:</strong> Environmental hazards dealing damage</li>
                <li><strong>Treasure Tiles:</strong> Contains loot chests</li>
                <li><strong>Boss Tile:</strong> Final challenge with best rewards</li>
                <li><strong>Exit Tile:</strong> Leave dungeon and keep all loot</li>
              </ul>

              <h4>Dungeon Strategy</h4>
              <ul>
                <li><strong>Check Recommended Level:</strong> Don't enter under-leveled</li>
                <li><strong>Upgrade Equipment First:</strong> Better gear = easier dungeon runs</li>
                <li><strong>Explore Carefully:</strong> Double-click tiles to move (prevents accidents)</li>
                <li><strong>Avoid Traps When Low HP:</strong> Traps deal percentage-based damage</li>
                <li><strong>Save Boss for Last:</strong> Clear other tiles first for full exploration</li>
                <li><strong>Exit Before Death:</strong> If HP is low, find exit tile to save loot</li>
              </ul>
            </>
          ),
        },
        'combat-strategy': {
          name: 'Combat Mechanics',
          icon: '⚔️',
          content: (
            <>
              <h3>Combat System</h3>
              <p>Combat in dungeons is automatic, but your stats determine the outcome.</p>

              <h4>⚔️ How Combat Works</h4>
              <ul>
                <li><strong>Auto-Battle:</strong> Combat happens automatically based on your stats</li>
                <li><strong>Attack Speed:</strong> Determines how often you attack (base 1.0× = 1 attack/sec)</li>
                <li><strong>Turn-Based:</strong> You and enemy take turns attacking</li>
                <li><strong>Critical Hits:</strong> Random chance to deal bonus damage based on crit stats</li>
                <li><strong>Accuracy/Evasion:</strong> Attacks can miss or be dodged</li>
              </ul>

              <h4>Damage Calculation</h4>
              <div className="guide-formula">
                <code>Damage = (Attack - Enemy Defense × 0.5) × Crit Multiplier</code>
              </div>

              <p><strong>Example:</strong></p>
              <ul>
                <li>Your Attack: 50</li>
                <li>Enemy Defense: 20</li>
                <li>Crit Multiplier: 1.0× (no crit) or 1.5× (crit)</li>
              </ul>

              <div className="guide-formula">
                <code>Normal Hit = (50 - 20 × 0.5) = 40 damage</code><br/>
                <code>Critical Hit = 40 × 1.5 = 60 damage</code>
              </div>

              <h4>Defense Mechanics</h4>
              <div className="guide-formula">
                <code>Damage Reduction = Enemy Attack - (Your Defense × 0.5)</code>
              </div>

              <p><strong>Example:</strong></p>
              <ul>
                <li>Enemy Attack: 30</li>
                <li>Your Defense: 20</li>
              </ul>

              <div className="guide-formula">
                <code>Damage Taken = 30 - (20 × 0.5) = 20 damage</code>
              </div>

              <h4>Combat Log</h4>
              <p>View detailed combat information in the <strong>Dungeon Panel</strong> (left side when in a dungeon):</p>
              <ul>
                <li>Your attacks and damage dealt</li>
                <li>Enemy attacks and damage taken</li>
                <li>Critical hit notifications</li>
                <li>Miss/dodge messages</li>
                <li>Victory/defeat messages</li>
                <li>Loot obtained</li>
              </ul>

              <div className="guide-chain">
                <p>💡 <strong>Strategy Tip:</strong> Check the recommended level before entering a dungeon. Going in under-leveled or under-equipped can lead to defeat!</p>
              </div>

              <h4>Death & Consequences</h4>
              <p>If your HP reaches 0 during combat:</p>
              <ul>
                <li>You are immediately ejected from the dungeon</li>
                <li>HP is restored to full automatically</li>
                <li>Partial loot is kept (some items may be lost)</li>
                <li>No permanent consequences - try again when stronger!</li>
                <li>No experience or level penalty</li>
              </ul>

              <h4>Combat Optimization</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Strategy</th><th>Method</th><th>Best For</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Tank Build</strong></td>
                    <td>High STR for HP and Defense, defensive equipment</td>
                    <td>Surviving tough bosses, lower-tier dungeons</td>
                  </tr>
                  <tr>
                    <td><strong>DPS Build</strong></td>
                    <td>High DEX and LUK for crit, offensive equipment</td>
                    <td>Fast clears, farming materials</td>
                  </tr>
                  <tr>
                    <td><strong>Speed Build</strong></td>
                    <td>High DEX for attack speed, fast weapons</td>
                    <td>Consistent DPS, multiple enemies</td>
                  </tr>
                  <tr>
                    <td><strong>Balanced Build</strong></td>
                    <td>Mix of all stats, versatile equipment</td>
                    <td>General purpose, first-time exploration</td>
                  </tr>
                </tbody>
              </table>

              <h4>Advanced Combat Tips</h4>
              <ul>
                <li><strong>Quality Matters:</strong> 100% quality equipment has 2× stats vs 50% quality</li>
                <li><strong>Level Scaling:</strong> Each level gives +10 HP, +2 ATK, +1 DEF automatically</li>
                <li><strong>Crit Stacking:</strong> LUK gives both crit chance AND crit damage - very powerful</li>
                <li><strong>Attack Speed Breakpoints:</strong> 1.5× attack speed = 50% more DPS</li>
                <li><strong>Gold/XP Gear:</strong> Swap to farmer gear before final boss for maximum rewards</li>
              </ul>
            </>
          ),
        },
        'character-quick-reference': {
          name: 'Quick Reference',
          icon: '📖',
          content: (
            <>
              <h3>Character Tab Quick Reference</h3>

              <table className="guide-table">
                <thead>
                  <tr><th>Action</th><th>How To</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>View Stats</strong></td>
                    <td>Open Character tab - stats displayed on left panel</td>
                  </tr>
                  <tr>
                    <td><strong>Equip Item</strong></td>
                    <td>Drag from inventory to equipment slot OR right-click item OR double-click item</td>
                  </tr>
                  <tr>
                    <td><strong>Unequip Item</strong></td>
                    <td>Click equipped item to move it back to inventory</td>
                  </tr>
                  <tr>
                    <td><strong>View Item Details</strong></td>
                    <td>Hover over any item for tooltip with full stats OR Ctrl+Click for detailed modal</td>
                  </tr>
                  <tr>
                    <td><strong>Find Dungeons</strong></td>
                    <td>Explore World Map - dungeons have purple borders</td>
                  </tr>
                  <tr>
                    <td><strong>Enter Dungeon</strong></td>
                    <td>Click dungeon tile → "Enter Dungeon" button</td>
                  </tr>
                  <tr>
                    <td><strong>Check Materials</strong></td>
                    <td>Resource Panel (Dungeon category) OR Character Inventory (Misc tab)</td>
                  </tr>
                  <tr>
                    <td><strong>Allocate Stat Points</strong></td>
                    <td>Click + button next to primary stat when you have stat points</td>
                  </tr>
                  <tr>
                    <td><strong>Use Consumable</strong></td>
                    <td>Double-click potion or food item in inventory</td>
                  </tr>
                  <tr>
                    <td><strong>Delete Items</strong></td>
                    <td>Click 🗑️ Delete button, select items with Shift+Click, confirm deletion</td>
                  </tr>
                  <tr>
                    <td><strong>Sort Inventory</strong></td>
                    <td>Use dropdown menu above inventory grid (Default, Rarity, Type, Name)</td>
                  </tr>
                  <tr>
                    <td><strong>Leave Dungeon</strong></td>
                    <td>Find exit tile (🚪) and double-click to leave with all loot</td>
                  </tr>
                </tbody>
              </table>

              <div className="guide-chain">
                <p>🎯 <strong>Ready to Adventure!</strong> You now know how to manage your character, equip items, and conquer dungeons. Good luck, adventurer!</p>
              </div>

              <h4>Keyboard Shortcuts</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Key</th><th>Action</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Ctrl + Click</strong></td>
                    <td>View detailed item information modal</td>
                  </tr>
                  <tr>
                    <td><strong>Shift + Click</strong></td>
                    <td>Select item (for multi-delete)</td>
                  </tr>
                  <tr>
                    <td><strong>Right Click</strong></td>
                    <td>Open item context menu</td>
                  </tr>
                  <tr>
                    <td><strong>Double Click</strong></td>
                    <td>Equip item (equipment) OR use item (consumables) OR move to tile (dungeons)</td>
                  </tr>
                  <tr>
                    <td><strong>Drag & Drop</strong></td>
                    <td>Move items between slots, equip items, organize inventory</td>
                  </tr>
                </tbody>
              </table>

              <h4>Common Questions</h4>
              <ul>
                <li><strong>Q: Why can't I delete dungeon materials?</strong><br/>
                A: Dungeon materials are protected and managed in the Resources panel. They're needed for technology research.</li>

                <li><strong>Q: How do I get better equipment?</strong><br/>
                A: Explore higher-tier dungeons and defeat bosses for better loot drops.</li>

                <li><strong>Q: What happens if I die in a dungeon?</strong><br/>
                A: You're ejected with partial loot, HP restored, no permanent penalty. Try again when stronger!</li>

                <li><strong>Q: Should I focus on one stat or spread points?</strong><br/>
                A: Depends on your playstyle. STR for tanking, DEX+LUK for DPS, INT for farming, or balanced for versatility.</li>

                <li><strong>Q: How do I know what equipment is better?</strong><br/>
                A: Hover for tooltips showing stat comparisons with currently equipped item (green = better, red = worse).</li>
              </ul>
            </>
          ),
        },
      },
    },
    'outposts-mercenaries': {
      name: 'Outposts & Mercenaries',
      icon: '🏕️',
      topics: {
        'outpost-overview': {
          name: 'Outpost System',
          icon: '⛺',
          content: (
            <>
              <h3>Outpost System Overview</h3>
              <p>Outposts are player-built settlements on world map tiles that extend your kingdom's influence and enable automated dungeon running through mercenaries.</p>

              <div className="guide-ability">
                <h4>🏕️ What Are Outposts?</h4>
                <p>Outposts are permanent structures you can build on discovered tiles. Unlike simple tile buildings, outposts offer:</p>
                <ul>
                  <li><strong>4 Building Slots:</strong> Build up to 4 buildings per outpost (vs 1 on regular tiles)</li>
                  <li><strong>Mercenary Stations:</strong> Hire and manage mercenaries for automated missions</li>
                  <li><strong>Custom Naming:</strong> Name your outpost whatever you like</li>
                  <li><strong>Instant Teleportation:</strong> Travel directly to any outpost from anywhere</li>
                </ul>
              </div>

              <h4>Building an Outpost</h4>
              <ol>
                <li><strong>Find a Tile:</strong> Discover a suitable world map tile (not dungeons, encounters, or Kingdom)</li>
                <li><strong>Click Tile:</strong> Select the tile to open the Tile Preview Panel</li>
                <li><strong>Create Outpost:</strong> Click the "Create Outpost" button</li>
                <li><strong>Name It:</strong> Enter a custom name for your outpost</li>
                <li><strong>Pay Costs:</strong> Resource costs vary by biome type</li>
                <li><strong>Wait for Construction:</strong> Outposts take time to build (30 seconds)</li>
              </ol>

              <h4>Outpost Limits</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Limit</th><th>Value</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Maximum Outposts</strong></td>
                    <td>3</td>
                    <td>May be expandable via future technologies</td>
                  </tr>
                  <tr>
                    <td><strong>Building Slots</strong></td>
                    <td>4 per outpost</td>
                    <td>Buildings match tile's biome compatibility</td>
                  </tr>
                  <tr>
                    <td><strong>Mercenary Capacity</strong></td>
                    <td>6 per outpost</td>
                    <td>Maximum mercenaries stationed</td>
                  </tr>
                  <tr>
                    <td><strong>Squad Size</strong></td>
                    <td>4 max</td>
                    <td>Mercenaries per mission squad</td>
                  </tr>
                </tbody>
              </table>

              <h4>Construction Costs by Biome</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Biome Tier</th><th>Examples</th><th>Typical Cost</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Tier 1 (Cheap)</strong></td>
                    <td>Forest, Plains</td>
                    <td>150-200 wood/stone, 50 gold</td>
                  </tr>
                  <tr>
                    <td><strong>Tier 2 (Moderate)</strong></td>
                    <td>Mountains, Coastal, Hills</td>
                    <td>200-300 resources, 75-150 gold</td>
                  </tr>
                  <tr>
                    <td><strong>Tier 3 (Expensive)</strong></td>
                    <td>Ruins, Volcanic, Swamp</td>
                    <td>400-500 resources, 150-200 gold</td>
                  </tr>
                  <tr>
                    <td><strong>Tier 4+ (Very Expensive)</strong></td>
                    <td>Mystic Grove, Desert, Void Rift</td>
                    <td>500-800 resources, 300-500 gold</td>
                  </tr>
                </tbody>
              </table>

              <h4>Outpost Features</h4>
              <div className="guide-chain">
                <p>📍 <strong>Fast Travel:</strong> Outposts appear in your teleport destinations. Click "Teleport to Outpost" to instantly travel there from any location.</p>
              </div>
              <div className="guide-chain">
                <p>🏗️ <strong>4× Building Power:</strong> Each outpost supports 4 buildings, making them excellent production hubs for resource-rich biomes.</p>
              </div>
              <div className="guide-chain">
                <p>⚔️ <strong>Mercenary Base:</strong> Station mercenaries at outposts to send them on automated dungeon missions while you play.</p>
              </div>
            </>
          ),
        },
        'mercenary-system': {
          name: 'Mercenaries',
          icon: '⚔️',
          content: (
            <>
              <h3>Mercenary System</h3>
              <p>Mercenaries are hireable units that can be stationed at outposts. They form squads to tackle dungeons automatically, earning rewards while you focus on other activities.</p>

              <div className="guide-ability">
                <h4>⚔️ What Are Mercenaries?</h4>
                <ul>
                  <li><strong>Automated Fighters:</strong> Send them on missions to clear dungeons</li>
                  <li><strong>Random Stats:</strong> Each mercenary has randomized stats like equipment quality</li>
                  <li><strong>Permadeath Risk:</strong> Mercenaries can die permanently on failed missions</li>
                  <li><strong>Class System:</strong> 4 distinct classes with different strengths</li>
                </ul>
              </div>

              <h4>Mercenary Classes</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Class</th><th>Icon</th><th>Specialty</th><th>Primary Stats</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Warrior</strong></td>
                    <td>⚔️</td>
                    <td>Heavy melee fighter, high HP and defense</td>
                    <td>STR (1.5×), DEX (0.8×)</td>
                  </tr>
                  <tr>
                    <td><strong>Ranger</strong></td>
                    <td>🏹</td>
                    <td>Agile ranged attacker, high accuracy</td>
                    <td>DEX (1.5×), LUK (1.0×)</td>
                  </tr>
                  <tr>
                    <td><strong>Mage</strong></td>
                    <td>🔮</td>
                    <td>Powerful spellcaster, high damage but low HP</td>
                    <td>INT (1.5×), LUK (0.8×)</td>
                  </tr>
                  <tr>
                    <td><strong>Rogue</strong></td>
                    <td>🗡️</td>
                    <td>Swift striker, high crit chance and luck</td>
                    <td>LUK (1.5×), DEX (1.2×)</td>
                  </tr>
                </tbody>
              </table>

              <h4>Hiring Mercenaries</h4>
              <ol>
                <li>Open an outpost's management modal</li>
                <li>Go to the <strong>Mercenaries</strong> tab</li>
                <li>Click <strong>"Hire Mercenary"</strong></li>
                <li>Select a class (Warrior, Ranger, Mage, or Rogue)</li>
                <li>Pay the hiring cost (500 gold base + 50 per level)</li>
              </ol>

              <h4>Mercenary Quality System</h4>
              <p>Like equipment, mercenaries have a <strong>quality roll</strong> that affects their stats:</p>
              <div className="guide-formula">
                <code>Quality Range: 0.6× to 1.4× stat multiplier</code>
              </div>
              <table className="guide-table">
                <thead>
                  <tr><th>Quality</th><th>Rating</th><th>Stars</th><th>Color</th></tr>
                </thead>
                <tbody>
                  <tr><td>1.30+</td><td>Legendary</td><td>★★★★★</td><td style={{color:'#fbbf24'}}>Gold</td></tr>
                  <tr><td>1.15-1.29</td><td>Epic</td><td>★★★★</td><td style={{color:'#a855f7'}}>Purple</td></tr>
                  <tr><td>1.00-1.14</td><td>Rare</td><td>★★★</td><td style={{color:'#3b82f6'}}>Blue</td></tr>
                  <tr><td>0.85-0.99</td><td>Uncommon</td><td>★★</td><td style={{color:'#22c55e'}}>Green</td></tr>
                  <tr><td>0.60-0.84</td><td>Common</td><td>★</td><td style={{color:'#9ca3af'}}>Gray</td></tr>
                </tbody>
              </table>

              <h4>Mercenary Stats</h4>
              <p>Mercenaries use the same 4 primary stats as your character:</p>
              <ul>
                <li><strong>STR:</strong> Health, Attack, Defense</li>
                <li><strong>DEX:</strong> Attack Speed, Accuracy, Evasion</li>
                <li><strong>INT:</strong> Defense, Special abilities</li>
                <li><strong>LUK:</strong> Critical chance, Critical damage</li>
              </ul>

              <div className="guide-chain">
                <p>💡 <strong>Tip:</strong> Legendary quality (1.3×+) mercenaries are extremely rare but have 40% better stats. They're worth keeping safe from risky missions!</p>
              </div>
            </>
          ),
        },
        'squad-missions': {
          name: 'Squad Missions',
          icon: '🗡️',
          content: (
            <>
              <h3>Squad Missions</h3>
              <p>Form squads from your mercenaries and send them on automated dungeon missions. Earn rewards passively while managing risk.</p>

              <h4>How Missions Work</h4>
              <ol>
                <li><strong>Form a Squad:</strong> Select 1-4 mercenaries from your outpost roster</li>
                <li><strong>Choose Target:</strong> Select a dungeon tier appropriate for your squad's power</li>
                <li><strong>Start Mission:</strong> Squad departs for the specified duration</li>
                <li><strong>Wait:</strong> Mission runs automatically (works offline)</li>
                <li><strong>Collect Results:</strong> Gather rewards (or face consequences of failure)</li>
              </ol>

              <h4>Mission Duration by Tier</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Dungeon Tier</th><th>Base Duration</th><th>Multiplier</th></tr>
                </thead>
                <tbody>
                  <tr><td>Tier 1 (Ancient Ruins)</td><td>5 minutes</td><td>1.0×</td></tr>
                  <tr><td>Tier 2 (Dark Catacombs)</td><td>7.5 minutes</td><td>1.5×</td></tr>
                  <tr><td>Tier 3 (Corrupted Temple)</td><td>10 minutes</td><td>2.0×</td></tr>
                  <tr><td>Tier 4 (Abyssal Depths)</td><td>15 minutes</td><td>3.0×</td></tr>
                  <tr><td>Tier 5 (Void Citadel)</td><td>20 minutes</td><td>4.0×</td></tr>
                </tbody>
              </table>

              <h4>Success Rate Calculation</h4>
              <div className="guide-formula">
                <code>Success Rate = Squad Power ÷ Recommended Power</code>
              </div>
              <table className="guide-table">
                <thead>
                  <tr><th>Power Ratio</th><th>Success Rate</th><th>Risk Level</th></tr>
                </thead>
                <tbody>
                  <tr><td>0.5× or below</td><td>10% (minimum)</td><td style={{color:'#ef4444'}}>Very High Risk</td></tr>
                  <tr><td>0.75×</td><td>~35%</td><td style={{color:'#f97316'}}>High Risk</td></tr>
                  <tr><td>1.0× (matched)</td><td>~55%</td><td style={{color:'#eab308'}}>Moderate Risk</td></tr>
                  <tr><td>1.25×</td><td>~75%</td><td style={{color:'#84cc16'}}>Low Risk</td></tr>
                  <tr><td>1.5× or above</td><td>95% (maximum)</td><td style={{color:'#22c55e'}}>Very Low Risk</td></tr>
                </tbody>
              </table>

              <h4>Mission Rewards</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Reward Type</th><th>Multiplier</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr><td>Loot/Materials</td><td>70%</td><td>Compared to manual dungeon run</td></tr>
                  <tr><td>Mercenary XP</td><td>50%</td><td>Mercs level up from missions</td></tr>
                  <tr><td>Player XP</td><td>30%</td><td>Passive XP gain for your character</td></tr>
                  <tr><td>Failure Loot</td><td>30%</td><td>Partial rewards even on failure</td></tr>
                </tbody>
              </table>

              <h4>⚠️ Permadeath Risk</h4>
              <div className="guide-ability">
                <p><strong>WARNING:</strong> When a mission FAILS, each mercenary has a <strong>30% chance to die permanently</strong>.</p>
                <ul>
                  <li>Dead mercenaries are removed forever</li>
                  <li>No way to resurrect them</li>
                  <li>Higher success rate = lower risk of failure = lower death chance</li>
                  <li>Only send valuable mercenaries on high-success missions!</li>
                </ul>
              </div>

              <h4>Mission Strategy</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Strategy</th><th>When to Use</th><th>Risk Level</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Safe Farming</strong></td>
                    <td>Strong squad vs weak dungeon (1.5×+ power)</td>
                    <td>5% failure, minimal death risk</td>
                  </tr>
                  <tr>
                    <td><strong>Balanced</strong></td>
                    <td>Matched power (1.0×)</td>
                    <td>~45% failure, moderate death risk</td>
                  </tr>
                  <tr>
                    <td><strong>High Risk/Reward</strong></td>
                    <td>Weak squad vs hard dungeon</td>
                    <td>High failure + death chance, better loot tier</td>
                  </tr>
                  <tr>
                    <td><strong>Expendable Squad</strong></td>
                    <td>Use common (1-star) mercs only</td>
                    <td>If they die, easy to replace</td>
                  </tr>
                </tbody>
              </table>

              <div className="guide-chain">
                <p>💀 <strong>Pro Tip:</strong> Keep your legendary (5-star) mercenaries for safe farming missions. Use common mercenaries to test difficult dungeons first!</p>
              </div>
            </>
          ),
        },
        'outpost-buildings': {
          name: 'Outpost Buildings',
          icon: '🏗️',
          content: (
            <>
              <h3>Outpost Building System</h3>
              <p>Each outpost has <strong>4 building slots</strong> that can be filled with any biome-compatible building.</p>

              <h4>Building Slot Mechanics</h4>
              <ul>
                <li><strong>4 Slots Per Outpost:</strong> Build up to 4 buildings in each outpost</li>
                <li><strong>Biome Matching:</strong> Available buildings depend on the outpost's tile biome</li>
                <li><strong>Full Production:</strong> Each building produces at 100% rate</li>
                <li><strong>Full Bonus:</strong> When all 4 slots are filled, gain +25% production bonus</li>
              </ul>

              <h4>Full Outpost Bonus</h4>
              <div className="guide-formula">
                <code>4/4 Buildings = +25% Production, +10% Gather Yield</code>
              </div>

              <h4>Strategic Building Placement</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Biome</th><th>Best Buildings</th><th>Strategy</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Forest</strong></td>
                    <td>Woodcutter, Forager, Hunter</td>
                    <td>Wood production hub</td>
                  </tr>
                  <tr>
                    <td><strong>Mountains</strong></td>
                    <td>Quarry, Mine, Smelter, Coal Mine</td>
                    <td>Stone/iron production chain</td>
                  </tr>
                  <tr>
                    <td><strong>Plains</strong></td>
                    <td>Farm, Windmill, Granary, Homestead</td>
                    <td>Food and population center</td>
                  </tr>
                  <tr>
                    <td><strong>Coastal</strong></td>
                    <td>Market, Harbor, Trading Post</td>
                    <td>Gold generation focus</td>
                  </tr>
                </tbody>
              </table>

              <div className="guide-chain">
                <p>🏗️ <strong>Tip:</strong> Place outposts on high-quality tiles (110%+) for both building production and gathering bonuses. A 130% quality mountain outpost is perfect for stone/iron!</p>
              </div>
            </>
          ),
        },
        'outpost-quick-reference': {
          name: 'Quick Reference',
          icon: '📋',
          content: (
            <>
              <h3>Outpost System Quick Reference</h3>

              <h4>Creating Outposts</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Action</th><th>How To</th></tr>
                </thead>
                <tbody>
                  <tr><td>Create Outpost</td><td>Select tile → Click "Create Outpost" → Name it → Pay costs</td></tr>
                  <tr><td>Open Outpost</td><td>Click outpost tile → Click "Manage Outpost"</td></tr>
                  <tr><td>Teleport to Outpost</td><td>From anywhere: use teleport menu or click outpost tile</td></tr>
                  <tr><td>Build in Outpost</td><td>Outpost modal → Buildings tab → Select slot → Choose building</td></tr>
                </tbody>
              </table>

              <h4>Managing Mercenaries</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Action</th><th>How To</th></tr>
                </thead>
                <tbody>
                  <tr><td>Hire Mercenary</td><td>Outpost modal → Mercenaries tab → "Hire" → Choose class</td></tr>
                  <tr><td>View Stats</td><td>Click mercenary card to see full stat breakdown</td></tr>
                  <tr><td>Dismiss Mercenary</td><td>Click mercenary → "Dismiss" button</td></tr>
                  <tr><td>Check Quality</td><td>Stars indicate quality (★ Common to ★★★★★ Legendary)</td></tr>
                </tbody>
              </table>

              <h4>Running Missions</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Action</th><th>How To</th></tr>
                </thead>
                <tbody>
                  <tr><td>Form Squad</td><td>Missions tab → Select mercenaries (1-4)</td></tr>
                  <tr><td>Start Mission</td><td>Choose dungeon tier → Click "Start Mission"</td></tr>
                  <tr><td>Check Progress</td><td>Timer shows remaining time</td></tr>
                  <tr><td>Collect Rewards</td><td>When complete, click to collect (or view casualties)</td></tr>
                </tbody>
              </table>

              <h4>Key Numbers</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Stat</th><th>Value</th></tr>
                </thead>
                <tbody>
                  <tr><td>Max Outposts</td><td>3</td></tr>
                  <tr><td>Buildings per Outpost</td><td>4</td></tr>
                  <tr><td>Mercs per Outpost</td><td>6</td></tr>
                  <tr><td>Max Squad Size</td><td>4</td></tr>
                  <tr><td>Construction Time</td><td>30 seconds</td></tr>
                  <tr><td>Mission Base Duration</td><td>5 minutes (Tier 1)</td></tr>
                  <tr><td>Death Chance on Failure</td><td>30% per merc</td></tr>
                  <tr><td>Min Success Rate</td><td>10%</td></tr>
                  <tr><td>Max Success Rate</td><td>95%</td></tr>
                </tbody>
              </table>
            </>
          ),
        },
      },
    },
    'equipment-enhancement': {
      name: 'Equipment Enhancement',
      icon: '✨',
      topics: {
        'star-force': {
          name: 'Star Force',
          icon: '⭐',
          content: (
            <>
              <h3>Star Force Enhancement System</h3>
              <p>Star Force is a MapleStory-inspired enhancement system that adds permanent stat bonuses to equipment through Star Essence currency.</p>

              <h4>How Star Force Works</h4>
              <ul>
                <li><strong>Max Stars:</strong> Each equipment can reach 15 stars maximum</li>
                <li><strong>No Destruction:</strong> Items are never destroyed, only downgrade on high-star failures</li>
                <li><strong>Currency:</strong> Star Essence (✦) - dropped from dungeon enemies</li>
                <li><strong>Stat Bonuses:</strong> Each star provides ATK, DEF, and HP bonuses</li>
              </ul>

              <h4>How to Enhance</h4>
              <ol>
                <li>Right-click any equipment item</li>
                <li>Select <strong>"Enhance"</strong> from the context menu</li>
                <li>The Star Force Modal opens showing current stars and success rate</li>
                <li>Click <strong>"Enhance"</strong> to attempt adding a star</li>
                <li>Success adds a star, failure may downgrade (at 6+ stars)</li>
              </ol>

              <h4>Success Rates</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Enhancement</th><th>Success Rate</th><th>Failure Effect</th></tr>
                </thead>
                <tbody>
                  <tr><td>0★ → 1★</td><td>95%</td><td>Stay at 0★</td></tr>
                  <tr><td>1★ → 2★</td><td>90%</td><td>Stay at 1★</td></tr>
                  <tr><td>2★ → 3★</td><td>85%</td><td>Stay at 2★</td></tr>
                  <tr><td>3★ → 4★</td><td>80%</td><td>Stay at 3★</td></tr>
                  <tr><td>4★ → 5★</td><td>75%</td><td>Stay at 4★</td></tr>
                  <tr><td>5★ → 6★</td><td>70%</td><td>Stay at 5★</td></tr>
                  <tr><td>6★ → 7★</td><td>65%</td><td>Drop to 5★</td></tr>
                  <tr><td>7★ → 8★</td><td>60%</td><td>Drop to 6★</td></tr>
                  <tr><td>8★ → 9★</td><td>55%</td><td>Drop to 7★</td></tr>
                  <tr><td>9★ → 10★</td><td>50%</td><td>Drop to 8★</td></tr>
                  <tr><td>10★ → 11★</td><td>45%</td><td>Drop to 9★</td></tr>
                  <tr><td>11★ → 12★</td><td>40%</td><td>Drop to 10★</td></tr>
                  <tr><td>12★ → 13★</td><td>35%</td><td>Drop to 11★</td></tr>
                  <tr><td>13★ → 14★</td><td>30%</td><td>Drop to 12★</td></tr>
                  <tr><td>14★ → 15★</td><td>25%</td><td>Drop to 13★</td></tr>
                </tbody>
              </table>

              <h4>Pity System</h4>
              <div className="guide-formula">
                <code>+10% success rate per consecutive failure (max +30%)</code>
              </div>
              <p>Each failure increases your next attempt's success rate by 10%, up to a maximum bonus of 30%. Resets on success.</p>

              <h4>Safeguard Mode</h4>
              <p>Available for stars 6-10, Safeguard prevents downgrade on failure:</p>
              <ul>
                <li><strong>Cost:</strong> 2× normal Star Essence cost</li>
                <li><strong>Effect:</strong> Failure keeps current star level instead of dropping</li>
                <li><strong>Best For:</strong> Protecting valuable 9★ or 10★ items</li>
              </ul>

              <h4>Star Force Cost Scaling</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Item Rarity</th><th>Cost Multiplier</th></tr>
                </thead>
                <tbody>
                  <tr><td>Common</td><td>0.5×</td></tr>
                  <tr><td>Uncommon</td><td>0.75×</td></tr>
                  <tr><td>Rare</td><td>1.0×</td></tr>
                  <tr><td>Epic</td><td>1.5×</td></tr>
                  <tr><td>Legendary</td><td>2.0×</td></tr>
                  <tr><td>Mythic</td><td>3.0×</td></tr>
                </tbody>
              </table>

              <h4>Milestone Bonuses</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Stars</th><th>Bonus</th></tr>
                </thead>
                <tbody>
                  <tr><td>5★</td><td>+2% Crit Chance</td></tr>
                  <tr><td>10★</td><td>+5% Crit Damage, +1% All Stats</td></tr>
                  <tr><td>15★</td><td>+5% Crit Chance, +10% Crit Damage, +3% All Stats</td></tr>
                </tbody>
              </table>

              <h4>Star Essence Drop Rates</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Source</th><th>Drop Chance</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  <tr><td>Tier 1 Mobs</td><td>30%</td><td>1-2</td></tr>
                  <tr><td>Tier 2 Mobs</td><td>40%</td><td>2-4</td></tr>
                  <tr><td>Tier 3 Mobs</td><td>50%</td><td>4-6</td></tr>
                  <tr><td>Tier 4 Mobs</td><td>60%</td><td>6-10</td></tr>
                  <tr><td>Tier 5 Mobs</td><td>70%</td><td>10-15</td></tr>
                  <tr><td>All Bosses</td><td>100%</td><td>15-250 (by tier)</td></tr>
                </tbody>
              </table>
            </>
          ),
        },
        'forging-scrolls': {
          name: 'Forging (Scrolls)',
          icon: '📜',
          content: (
            <>
              <h3>Forging System (Scroll Enhancement)</h3>
              <p>The Forging system uses scrolls and gemstones to add permanent stat bonuses to equipment slots. Unlike Star Force, scrolls target specific slots and can fail permanently.</p>

              <h4>How Forging Works</h4>
              <ul>
                <li><strong>Scroll Slots:</strong> Each equipment has limited scroll slots based on type</li>
                <li><strong>Gemstone Currency:</strong> 5 colored gemstones, each for different scroll types</li>
                <li><strong>Permanent Effects:</strong> Successful scrolls add stats forever</li>
                <li><strong>Slot Destruction:</strong> Failed scrolls permanently destroy the slot (authentic MapleStory)</li>
              </ul>

              <h4>Scroll Slots by Equipment Type</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Equipment Slot</th><th>Max Scroll Slots</th></tr>
                </thead>
                <tbody>
                  <tr><td>Weapon</td><td>7</td></tr>
                  <tr><td>Top / Bottom</td><td>10</td></tr>
                  <tr><td>Helmet / Gloves / Boots / Cape / Belt / Offhand</td><td>7</td></tr>
                  <tr><td>Ring / Pendant</td><td>5</td></tr>
                </tbody>
              </table>

              <h4>Gemstone Types</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Gemstone</th><th>Icon</th><th>Scroll Type</th><th>Stat Bonus</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>Ruby</strong></td><td>🔴</td><td>Attack Scroll</td><td>+ATK</td></tr>
                  <tr><td><strong>Sapphire</strong></td><td>🔵</td><td>Defense Scroll</td><td>+DEF</td></tr>
                  <tr><td><strong>Emerald</strong></td><td>🟢</td><td>Health Scroll</td><td>+HP</td></tr>
                  <tr><td><strong>Topaz</strong></td><td>🟡</td><td>Critical Scroll</td><td>+Crit%</td></tr>
                  <tr><td><strong>Amethyst</strong></td><td>🟣</td><td>Speed Scroll</td><td>+SPD</td></tr>
                </tbody>
              </table>

              <h4>Gem Tiers & Success Rates</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Tier</th><th>Success</th><th>ATK/DEF</th><th>HP</th><th>Crit</th><th>SPD</th><th>Cost</th></tr>
                </thead>
                <tbody>
                  <tr><td>Tier 1</td><td>90%</td><td>+1</td><td>+10</td><td>+1%</td><td>+1</td><td>1 gem</td></tr>
                  <tr><td>Tier 2</td><td>70%</td><td>+2</td><td>+25</td><td>+2%</td><td>+1</td><td>3 gems</td></tr>
                  <tr><td>Tier 3</td><td>50%</td><td>+4</td><td>+50</td><td>+3%</td><td>+2</td><td>8 gems</td></tr>
                  <tr><td>Tier 4</td><td>30%</td><td>+7</td><td>+100</td><td>+5%</td><td>+3</td><td>20 gems</td></tr>
                  <tr><td>Tier 5</td><td>15%</td><td>+12</td><td>+200</td><td>+8%</td><td>+4</td><td>50 gems</td></tr>
                </tbody>
              </table>

              <h4>How to Forge</h4>
              <ol>
                <li>Right-click equipment and select <strong>"Forge"</strong></li>
                <li>Or open the <strong>Forge tab</strong> in the center panel</li>
                <li>Select equipment to forge</li>
                <li>Choose scroll tier (higher = better stats but lower success)</li>
                <li>Choose scroll type (ATK, DEF, HP, Crit, or SPD)</li>
                <li>Click <strong>"Apply Scroll"</strong></li>
              </ol>

              <h4>Slot Status Display</h4>
              <ul>
                <li><strong>○</strong> - Empty slot (available)</li>
                <li><strong>1-5</strong> - Used slot showing scroll tier</li>
                <li><strong>X</strong> - Destroyed slot (permanent)</li>
              </ul>

              <h4>Gemstone Drop Rates</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Source</th><th>Drop Chance (each)</th><th>Amount</th></tr>
                </thead>
                <tbody>
                  <tr><td>Tier 1 Mobs</td><td>8%</td><td>1</td></tr>
                  <tr><td>Tier 2 Mobs</td><td>12%</td><td>1-2</td></tr>
                  <tr><td>Tier 3 Mobs</td><td>18%</td><td>1-3</td></tr>
                  <tr><td>Tier 4 Mobs</td><td>25%</td><td>2-4</td></tr>
                  <tr><td>Tier 5 Mobs</td><td>35%</td><td>3-6</td></tr>
                  <tr><td>All Bosses</td><td>100%</td><td>5-30 (by tier)</td></tr>
                </tbody>
              </table>

              <div className="guide-chain">
                <p>⚠️ <strong>Warning:</strong> Failed scrolls permanently destroy slots! For valuable equipment, start with Tier 1 scrolls (90% success) to safely fill slots, then save Tier 5 for less important items.</p>
              </div>
            </>
          ),
        },
        'cubing-potential': {
          name: 'Cubing (Potential)',
          icon: '🎲',
          content: (
            <>
              <h3>Cubing System (Equipment Potential)</h3>
              <p>The Cubing system adds randomized percentage-based bonus stats called "potential" to equipment. Higher grades unlock more powerful stats.</p>

              <h4>What is Potential?</h4>
              <ul>
                <li><strong>Bonus Lines:</strong> 1-3 lines of percentage stats per item</li>
                <li><strong>Grades:</strong> Rare, Epic, Unique, Legendary (higher = better stats)</li>
                <li><strong>Slot-Based:</strong> Different equipment slots have different potential pools</li>
                <li><strong>Cubes:</strong> Use cubes to reroll or upgrade potential</li>
              </ul>

              <h4>Potential Grades</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Grade</th><th>Color</th><th>Lines</th><th>Stat Range</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>Rare</strong></td><td style={{color:'#3b82f6'}}>Blue</td><td>1-2</td><td>1-3%</td></tr>
                  <tr><td><strong>Epic</strong></td><td style={{color:'#a855f7'}}>Purple</td><td>2-3</td><td>3-6%</td></tr>
                  <tr><td><strong>Unique</strong></td><td style={{color:'#fbbf24'}}>Gold</td><td>3</td><td>6-9%</td></tr>
                  <tr><td><strong>Legendary</strong></td><td style={{color:'#22c55e'}}>Green</td><td>3</td><td>9-12%</td></tr>
                </tbody>
              </table>

              <h4>Potential Stats by Slot</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Equipment Type</th><th>Available Potential Stats</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>Weapons</strong></td><td>ATK%, Boss Damage%, Ignore DEF%, STR%</td></tr>
                  <tr><td><strong>Armor</strong></td><td>DEF%, HP%, STR%, DEX%, INT%, LUK%, All Stats%</td></tr>
                  <tr><td><strong>Accessories</strong></td><td>All Stats%, Crit Chance%, Crit Damage%</td></tr>
                </tbody>
              </table>

              <h4>Cube Types</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Cube</th><th>Effect</th><th>Use Case</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Red Cube</strong></td>
                    <td>Reroll potential lines (keep current grade)</td>
                    <td>Find better stats within current grade</td>
                  </tr>
                  <tr>
                    <td><strong>Black Cube</strong></td>
                    <td>Chance to upgrade grade + reroll</td>
                    <td>Upgrade Rare → Epic → Unique → Legendary</td>
                  </tr>
                  <tr>
                    <td><strong>Bonus Cube</strong></td>
                    <td>Add or reroll bonus potential</td>
                    <td>Additional potential lines on top of main</td>
                  </tr>
                </tbody>
              </table>

              <h4>How to Cube</h4>
              <ol>
                <li>Right-click equipment and select <strong>"Cube"</strong></li>
                <li>View current potential grade and lines</li>
                <li>Select cube type (Red, Black, or Bonus)</li>
                <li>Click <strong>"Use Cube"</strong></li>
                <li>View results and choose to keep or reroll again</li>
              </ol>

              <div className="guide-chain">
                <p>💡 <strong>Strategy:</strong> Use Black Cubes to upgrade to Legendary grade first, then use Red Cubes to reroll for perfect stats. Never waste Red Cubes on low-grade items!</p>
              </div>
            </>
          ),
        },
        'enhancement-quick-reference': {
          name: 'Quick Reference',
          icon: '📋',
          content: (
            <>
              <h3>Enhancement Systems Quick Reference</h3>

              <h4>System Comparison</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>System</th><th>Currency</th><th>Max Level</th><th>Risk</th><th>Best For</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Star Force</strong></td>
                    <td>Star Essence (✦)</td>
                    <td>15 stars</td>
                    <td>Downgrade at 6+</td>
                    <td>Flat stat bonuses</td>
                  </tr>
                  <tr>
                    <td><strong>Forging</strong></td>
                    <td>Gemstones (5 types)</td>
                    <td>5-10 slots</td>
                    <td>Permanent slot loss</td>
                    <td>Targeted stat boosts</td>
                  </tr>
                  <tr>
                    <td><strong>Cubing</strong></td>
                    <td>Cubes (3 types)</td>
                    <td>Legendary grade</td>
                    <td>RNG rerolls</td>
                    <td>Percentage bonuses</td>
                  </tr>
                </tbody>
              </table>

              <h4>Recommended Enhancement Order</h4>
              <ol>
                <li><strong>Cubing First:</strong> Get equipment to Unique/Legendary grade before investing in other systems</li>
                <li><strong>Forging Second:</strong> Use Tier 1-2 scrolls (high success) to safely add stats</li>
                <li><strong>Star Force Last:</strong> Expensive and RNG-heavy, save for endgame</li>
              </ol>

              <h4>Currency Sources</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Currency</th><th>Source</th><th>Best Farm</th></tr>
                </thead>
                <tbody>
                  <tr><td>Star Essence</td><td>Dungeon mobs/bosses</td><td>Tier 4-5 dungeons, bosses</td></tr>
                  <tr><td>Gemstones</td><td>Dungeon mobs/bosses</td><td>Tier 3+ dungeons, bosses</td></tr>
                  <tr><td>Cubes</td><td>Crafting, events, bosses</td><td>Boss rewards, special events</td></tr>
                </tbody>
              </table>

              <h4>Actions Quick Reference</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Action</th><th>How To</th></tr>
                </thead>
                <tbody>
                  <tr><td>Star Force Enhance</td><td>Right-click item → "Enhance" → Click "Enhance" button</td></tr>
                  <tr><td>Apply Scroll</td><td>Right-click item → "Forge" → Select tier → Apply</td></tr>
                  <tr><td>Use Cube</td><td>Right-click item → "Cube" → Select cube type → Use</td></tr>
                  <tr><td>Toggle Safeguard</td><td>In Star Force modal, check "Safeguard" (6-10★ only)</td></tr>
                </tbody>
              </table>
            </>
          ),
        },
      },
    },
    'great-works-trade': {
      name: 'Great Works & Trade',
      icon: '🗿',
      topics: {
        'great-works-overview': {
          name: 'Great Works',
          icon: '🗿',
          content: (
            <>
              <h3>Great Works System</h3>
              <p>Great Works are powerful map-bound structures that require specific tile cluster placements. Unlike regular Wonders, Great Works must be built on tiles that meet biome requirements.</p>

              <h4>What Are Great Works?</h4>
              <ul>
                <li><strong>Map-Placed:</strong> Built on specific world map tiles (not in kingdom)</li>
                <li><strong>Tile Requirements:</strong> Must match biome category or specific biomes</li>
                <li><strong>Cluster Bonuses:</strong> Nearby matching tiles increase effectiveness</li>
                <li><strong>Limited Slots:</strong> Maximum 5 Great Works total</li>
                <li><strong>50% Cancel Refund:</strong> Same as regular Wonders</li>
              </ul>

              <h4>Biome Categories</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Category</th><th>Biomes Included</th><th>Associated Great Works</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Nature</strong></td>
                    <td>Forest, Plains, Grassland, Swamp</td>
                    <td>Ancient Grove, World Tree</td>
                  </tr>
                  <tr>
                    <td><strong>Stone</strong></td>
                    <td>Mountains, Rocky Outcrop, Hills</td>
                    <td>Mountain Fortress, Stone Monument</td>
                  </tr>
                  <tr>
                    <td><strong>Water</strong></td>
                    <td>Coastal, River, Lake, Ocean</td>
                    <td>Grand Harbor, Tidal Palace</td>
                  </tr>
                  <tr>
                    <td><strong>Knowledge</strong></td>
                    <td>Ruins, Library, Shrine</td>
                    <td>Grand Archive, Wisdom Spire</td>
                  </tr>
                  <tr>
                    <td><strong>Magical</strong></td>
                    <td>Mystic Grove, Crystal Cavern</td>
                    <td>Arcane Nexus, Mana Well</td>
                  </tr>
                  <tr>
                    <td><strong>Hostile</strong></td>
                    <td>Volcanic, Tundra, Desert</td>
                    <td>Fortress of Extremes</td>
                  </tr>
                  <tr>
                    <td><strong>Exotic</strong></td>
                    <td>Void Rift, Crystal Cavern, Oasis</td>
                    <td>Dimensional Gate</td>
                  </tr>
                </tbody>
              </table>

              <h4>Placement Requirements</h4>
              <p>Each Great Work has two requirements:</p>
              <ul>
                <li><strong>Anchor Tile:</strong> The tile where you place the Great Work (must match category)</li>
                <li><strong>Nearby Tiles:</strong> Surrounding tiles within range that boost effects</li>
              </ul>

              <h4>Example: Ancient Grove</h4>
              <table className="guide-table">
                <tbody>
                  <tr><td><strong>Anchor Requirement</strong></td><td>Nature category (Forest, Plains, etc.)</td></tr>
                  <tr><td><strong>Nearby Requirement</strong></td><td>3+ Nature tiles within 2 hexes</td></tr>
                  <tr><td><strong>Effect</strong></td><td>+45% Wood, +30% Food production</td></tr>
                </tbody>
              </table>

              <h4>How to Build Great Works</h4>
              <ol>
                <li>Open the <strong>Monuments</strong> tab (center panel)</li>
                <li>Switch to the <strong>Great Works</strong> sub-tab</li>
                <li>Select a Great Work to view its requirements</li>
                <li>Click <strong>"Place on Map"</strong> to enter placement mode</li>
                <li>Valid tiles glow purple on the world map</li>
                <li>Click a valid tile to start construction</li>
                <li>Wait for construction to complete</li>
              </ol>

              <div className="guide-chain">
                <p>💡 <strong>Strategy:</strong> Scout the map for tile clusters before committing to a Great Work. A mountain cluster with 5+ stone tiles is perfect for Stone-category Great Works!</p>
              </div>
            </>
          ),
        },
        'wonder-sites': {
          name: 'Wonder Sites',
          icon: '⛩️',
          content: (
            <>
              <h3>Wonder Sites</h3>
              <p>Wonder Sites are special 3-tile triangular formations on the world map that unlock site-bound Wonders.</p>

              <h4>What Are Wonder Sites?</h4>
              <ul>
                <li><strong>3-Tile Clusters:</strong> Triangular formations of adjacent hex tiles</li>
                <li><strong>Site-Bound Wonders:</strong> Certain Wonders can only be built after discovering their site</li>
                <li><strong>Visual Effects:</strong> Glowing borders and center medallions</li>
                <li><strong>Auto-Reveal:</strong> Discovering any 1 tile reveals all 3</li>
              </ul>

              <h4>Finding Wonder Sites</h4>
              <ul>
                <li><strong>Spawn Distance:</strong> Must be 10+ tiles from Kingdom (0,0)</li>
                <li><strong>Guaranteed:</strong> 1 wonder site per tier (weighted random)</li>
                <li><strong>Pity Timer:</strong> Forces spawn after 80% of exploration limit</li>
                <li><strong>Visual Indicator:</strong> Glowing hex borders in wonder-matched colors</li>
              </ul>

              <h4>Site-Bound Wonders by Tier</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Tier</th><th>Era</th><th>Wonders</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Tier 1</strong></td>
                    <td>Stone Age</td>
                    <td>Stonehenge, Great Bonfire, Tribal Monument</td>
                  </tr>
                  <tr>
                    <td><strong>Tier 2</strong></td>
                    <td>Bronze Age</td>
                    <td>Great Granary, Stone Citadel, Sacred Grove</td>
                  </tr>
                  <tr>
                    <td><strong>Tier 3</strong></td>
                    <td>Iron Age</td>
                    <td>Hanging Gardens, Great Library, Colosseum</td>
                  </tr>
                </tbody>
              </table>

              <h4>Wonder Site Visuals</h4>
              <ul>
                <li><strong>Hex Border Glow:</strong> Each tile has a colored glow matching the wonder</li>
                <li><strong>Center Medallion:</strong> Wonder icon displayed at triangle's center</li>
                <li><strong>Color Examples:</strong> Stonehenge (brown), Hanging Gardens (green), etc.</li>
              </ul>

              <h4>UI Integration</h4>
              <ul>
                <li><strong>Location Card:</strong> Shows wonder details when standing on site</li>
                <li><strong>Tile Preview:</strong> Shows wonder effects preview</li>
                <li><strong>Wonder Panel:</strong> Shows "Site Discovered" status for site-bound wonders</li>
              </ul>

              <div className="guide-chain">
                <p>⛩️ <strong>Tip:</strong> Wonder sites auto-reveal all 3 tiles when you discover any 1 of them. If you see a glowing tile, explore it to reveal the full site!</p>
              </div>
            </>
          ),
        },
        'trade-routes': {
          name: 'Trade Routes',
          icon: '🛤️',
          content: (
            <>
              <h3>Trade Route System</h3>
              <p>Trade Routes connect distant tiles to your Kingdom, unlocking <strong>building capacity</strong> in the Buildings Panel.</p>

              <h4>Why Trade Routes Matter</h4>
              <div className="guide-chain">
                <p>⚠️ <strong>Critical:</strong> Each tile building grants <strong>+5 panel capacity</strong> for that building type, but <strong>only when connected via trade route</strong>! Unconnected tile buildings don't count toward capacity.</p>
              </div>

              <h4>How Trade Routes Work</h4>
              <ul>
                <li><strong>Connectivity:</strong> BFS flood-fill from Kingdom (0,0)</li>
                <li><strong>Route Segments:</strong> Build segments on tiles to extend network</li>
                <li><strong>Building Capacity:</strong> Connected tile buildings grant +5 panel capacity each</li>
                <li><strong>Visual Overlay:</strong> Toggle shows golden animated lines</li>
                <li><strong>Distance Costs:</strong> Further tiles cost more to connect</li>
              </ul>

              <h4>Building Trade Routes</h4>
              <ol>
                <li>Select a tile on the world map</li>
                <li>Open the Tile Preview Panel</li>
                <li>If "Trade Route: None" is shown, click to build a segment</li>
                <li>Pay the distance-based cost</li>
                <li>Repeat to extend the route further</li>
              </ol>

              <h4>Trade Route Status</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Status</th><th>Icon</th><th>Meaning</th><th>Capacity Granted</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>Connected</strong></td><td>✅</td><td>Full connection to Kingdom</td><td>+5 per tile building</td></tr>
                  <tr><td><strong>Partial</strong></td><td>🔗</td><td>Route exists but not fully connected</td><td>None (must connect!)</td></tr>
                  <tr><td><strong>None</strong></td><td>—</td><td>No route segment built</td><td>None</td></tr>
                </tbody>
              </table>

              <h4>Distance Cost Scaling</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Hex Distance</th><th>Cost Multiplier</th></tr>
                </thead>
                <tbody>
                  <tr><td>1-5 tiles</td><td>1.0×</td></tr>
                  <tr><td>6-10 tiles</td><td>1.5×</td></tr>
                  <tr><td>11-20 tiles</td><td>2.0×</td></tr>
                  <tr><td>21-50 tiles</td><td>3.0×</td></tr>
                  <tr><td>50+ tiles</td><td>5.0×</td></tr>
                </tbody>
              </table>

              <h4>Technology Discounts</h4>
              <p>Research technologies to reduce trade route costs:</p>
              <ul>
                <li><strong>Road Building:</strong> -10% route costs</li>
                <li><strong>Trade Networks:</strong> -15% route costs</li>
                <li><strong>Infrastructure:</strong> -20% route costs</li>
              </ul>

              <h4>Trade Route Visualization</h4>
              <ul>
                <li><strong>Toggle Button:</strong> 🛤️ button below exploration limit panel</li>
                <li><strong>Visual Effect:</strong> Golden animated lines flowing toward Kingdom</li>
                <li><strong>Connected Tiles:</strong> Highlighted when overlay is active</li>
              </ul>

              <div className="guide-chain">
                <p>🛤️ <strong>Strategy:</strong> Connect tiles with buildings first to unlock panel capacity! Each connected tile building grants +5 capacity in the Buildings Panel. Also connect outposts for mercenary missions.</p>
              </div>
            </>
          ),
        },
      },
    },
    'life-skills': {
      name: 'Life Skills',
      icon: '⛏️',
      topics: {
        'life-skills-overview': {
          name: 'Life Skills Overview',
          icon: '📊',
          content: (
            <>
              <h3>Life Skills System</h3>
              <p>Life Skills are gathering and crafting proficiencies that improve as you collect resources and create items. Higher levels unlock bonuses and special abilities.</p>

              <h4>Skill Categories</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Category</th><th>Skills</th><th>Activities</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Gathering (5)</strong></td>
                    <td>Lumbering, Mining, Foraging, Prospecting, Archaeology</td>
                    <td>Collecting resources from world map tiles</td>
                  </tr>
                  <tr>
                    <td><strong>Crafting (2)</strong></td>
                    <td>Smithing, Enchanting</td>
                    <td>Creating and enhancing items</td>
                  </tr>
                </tbody>
              </table>

              <h4>Skill-Resource Mapping</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Skill</th><th>Resources Affected</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>Lumbering</strong></td><td>Wood</td></tr>
                  <tr><td><strong>Mining</strong></td><td>Stone, Iron, Coal, Crystite, Star Metal, Cosmic Ore</td></tr>
                  <tr><td><strong>Foraging</strong></td><td>Food, Herbs</td></tr>
                  <tr><td><strong>Prospecting</strong></td><td>Gold</td></tr>
                  <tr><td><strong>Archaeology</strong></td><td>Knowledge, Ancient Artifacts</td></tr>
                  <tr><td><strong>Smithing</strong></td><td>Equipment crafting, Forging success</td></tr>
                  <tr><td><strong>Enchanting</strong></td><td>Enhancement success (Star Force)</td></tr>
                </tbody>
              </table>

              <h4>XP and Leveling</h4>
              <div className="guide-formula">
                <code>XP to Level = 100 × 1.15^(Level-1)</code>
              </div>
              <table className="guide-table">
                <thead>
                  <tr><th>Level</th><th>XP Required</th><th>Cumulative XP</th></tr>
                </thead>
                <tbody>
                  <tr><td>1 → 2</td><td>100</td><td>100</td></tr>
                  <tr><td>5 → 6</td><td>175</td><td>750</td></tr>
                  <tr><td>10 → 11</td><td>405</td><td>2,250</td></tr>
                  <tr><td>25 → 26</td><td>3,292</td><td>32,000</td></tr>
                  <tr><td>50 (max)</td><td>—</td><td>~650,000</td></tr>
                </tbody>
              </table>

              <h4>How to Gain XP</h4>
              <div className="guide-formula">
                <code>XP Gained = Base XP × Amount × Tile Quality</code>
              </div>
              <p>Each time you gather resources from a tile:</p>
              <ul>
                <li>Gain XP in the relevant gathering skill</li>
                <li>Higher quality tiles give more XP</li>
                <li>Multiple resources = XP in multiple skills</li>
              </ul>
            </>
          ),
        },
        'milestone-bonuses': {
          name: 'Milestone Bonuses',
          icon: '🏆',
          content: (
            <>
              <h3>Life Skill Milestone Bonuses</h3>
              <p>Every 5 levels in a Life Skill unlocks a milestone bonus. These bonuses stack and apply permanently.</p>

              <h4>Gathering Skill Milestones</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Level</th><th>Yield Bonus</th><th>Rare Drop</th><th>Auto-Gather</th></tr>
                </thead>
                <tbody>
                  <tr><td>5</td><td>+5%</td><td>+1%</td><td>+5%</td></tr>
                  <tr><td>10</td><td>+10%</td><td>+3%</td><td>+10%</td></tr>
                  <tr><td>15</td><td>+18%</td><td>+5%</td><td>+15%</td></tr>
                  <tr><td>20</td><td>+25%</td><td>+7%</td><td>+18%</td></tr>
                  <tr><td>25</td><td>+33%</td><td>+9%</td><td>+21%</td></tr>
                  <tr><td>30</td><td>+40%</td><td>+11%</td><td>+24%</td></tr>
                  <tr><td>35</td><td>+48%</td><td>+13%</td><td>+26%</td></tr>
                  <tr><td>40</td><td>+55%</td><td>+14%</td><td>+27%</td></tr>
                  <tr><td>45</td><td>+62%</td><td>+14.5%</td><td>+29%</td></tr>
                  <tr><td>50</td><td>+65%</td><td>+15%</td><td>+30%</td></tr>
                </tbody>
              </table>

              <h4>Smithing Milestones</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Level</th><th>Forge Success</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr><td>5</td><td>+1%</td><td>All scroll success rates</td></tr>
                  <tr><td>10</td><td>+2%</td><td>Stacks with previous</td></tr>
                  <tr><td>25</td><td>+5%</td><td>Total: +5% to all scrolls</td></tr>
                  <tr><td>50</td><td>+8%</td><td>Total: +8% to all scrolls</td></tr>
                </tbody>
              </table>

              <h4>Enchanting Milestones</h4>
              <table className="guide-table">
                <thead>
                  <tr><th>Level</th><th>Enhance Success</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  <tr><td>5</td><td>+0.5%</td><td>Star Force success rates</td></tr>
                  <tr><td>10</td><td>+1%</td><td>Stacks with previous</td></tr>
                  <tr><td>25</td><td>+2.5%</td><td>Total: +2.5% to Star Force</td></tr>
                  <tr><td>50</td><td>+4%</td><td>Total: +4% to Star Force</td></tr>
                </tbody>
              </table>

              <h4>Viewing Life Skills</h4>
              <ol>
                <li>Open the <strong>Character</strong> tab</li>
                <li>Click the <strong>Life Skills</strong> toggle button</li>
                <li>Expandable panel shows all skills with:</li>
                <ul>
                  <li>Current level and XP bar</li>
                  <li>Active milestone bonuses</li>
                  <li>Next milestone preview</li>
                </ul>
              </ol>

              <div className="guide-chain">
                <p>💡 <strong>Tip:</strong> Focus on one gathering skill at a time by farming specific biome types. Level 25 Mining gives +33% stone/iron yield and +9% rare drops!</p>
              </div>
            </>
          ),
        },
      },
    },
  };

  // Flatten guide content for search
  const allTopics = useMemo(() => {
    const topics = [];
    Object.entries(guideContent).forEach(([catId, category]) => {
      Object.entries(category.topics).forEach(([topicId, topic]) => {
        // Extract text from JSX content for comprehensive search
        const contentText = extractTextFromJSX(topic.content);
        topics.push({
          categoryId: catId,
          categoryName: category.name,
          topicId,
          topicName: topic.name,
          icon: topic.icon,
          searchText: `${category.name} ${topic.name} ${contentText}`.toLowerCase(),
        });
      });
    });
    return topics;
  }, []);

  // Filter topics by search query
  const filteredTopics = useMemo(() => {
    if (!searchQuery.trim()) return allTopics;
    const query = searchQuery.toLowerCase();
    return allTopics.filter(t => t.searchText.includes(query));
  }, [searchQuery, allTopics]);

  // Auto-select first search result when searching
  React.useEffect(() => {
    if (searchQuery && filteredTopics.length > 0) {
      const firstResult = filteredTopics[0];
      setSelectedCategory(firstResult.categoryId);
      setSelectedTopic(firstResult.topicId);
    }
  }, [searchQuery, filteredTopics]);

  const currentCategory = guideContent[selectedCategory];
  const currentTopic = currentCategory?.topics[selectedTopic];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="guide-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="guide-header">
          <div className="guide-title">
            <span className="guide-icon">📖</span>
            <h2>Civilopedia</h2>
          </div>
          <button className="guide-close" onClick={onClose}>✕</button>
        </div>

        {/* Main Content */}
        <div className="guide-content">
          {/* Left Sidebar */}
          <div className="guide-sidebar">
            {/* Search Bar */}
            <div className="guide-search">
              <input
                type="text"
                placeholder="Search guide..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="guide-search-input"
              />
              {searchQuery && (
                <button
                  className="guide-search-clear"
                  onClick={() => setSearchQuery('')}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Category List or Search Results */}
            {searchQuery ? (
              <div className="guide-search-results">
                <div className="guide-search-count">
                  {filteredTopics.length} result{filteredTopics.length !== 1 ? 's' : ''}
                </div>
                {filteredTopics.map((topic) => (
                  <button
                    key={`${topic.categoryId}-${topic.topicId}`}
                    className={`guide-search-result ${
                      selectedCategory === topic.categoryId && selectedTopic === topic.topicId
                        ? 'active'
                        : ''
                    }`}
                    onClick={() => {
                      setSelectedCategory(topic.categoryId);
                      setSelectedTopic(topic.topicId);
                    }}
                  >
                    <span className="guide-result-icon">{topic.icon}</span>
                    <div className="guide-result-text">
                      <div className="guide-result-name">{topic.topicName}</div>
                      <div className="guide-result-category">{topic.categoryName}</div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="guide-categories">
                {Object.entries(guideContent).map(([catId, category]) => (
                  <div key={catId} className="guide-category">
                    <button
                      className={`guide-category-btn ${selectedCategory === catId ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedCategory(catId);
                        const firstTopic = Object.keys(category.topics)[0];
                        setSelectedTopic(firstTopic);
                      }}
                    >
                      <span className="guide-cat-icon">{category.icon}</span>
                      <span className="guide-cat-name">{category.name}</span>
                    </button>

                    {selectedCategory === catId && (
                      <div className="guide-topics">
                        {Object.entries(category.topics).map(([topicId, topic]) => (
                          <button
                            key={topicId}
                            className={`guide-topic-btn ${selectedTopic === topicId ? 'active' : ''}`}
                            onClick={() => setSelectedTopic(topicId)}
                          >
                            <span className="guide-topic-icon">{topic.icon}</span>
                            <span className="guide-topic-name">{topic.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Panel - Topic Content */}
          <div className="guide-panel">
            {currentTopic ? (
              <div className="guide-topic-content">
                {currentTopic.content}
              </div>
            ) : (
              <div className="guide-empty">
                <div className="guide-empty-icon">📖</div>
                <p>Select a topic to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GuideModal;
